import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useParams,
  useSearchParams } from 'react-router-dom';
import {
  Alert,
  AspectRatio,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Link,
  Image,
  Input,
  Portal,
  NativeSelect,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  ButtonGroup,
  Badge,
  useDisclosure,
  Slider,
  Menu,
  Separator,
  Field,
  Dialog,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { SliderTrack, SliderFilledTrack, SliderThumb, SliderRoot } from '@/components/ui/chakra-v2-compat';
import { toaster } from '@/components/ui/toaster';
import {
  FaFolder,
  FaList,
  FaTh,
  FaStickyNote,
  FaSearchPlus,
  FaSearchMinus,
  FaDownload,
  FaClipboardList,
  FaExclamationTriangle,
  FaYoutube,
  FaUser,
  FaSignOutAlt,
  FaTimes,
} from 'react-icons/fa';
import LoadingScreen from './LoadingScreen.jsx';
import Footer from './Footer.jsx';
import SEO from './SEO.jsx';
import RepertorioIncidenciaReportModal from './RepertorioIncidenciaReportModal.jsx';
import { repertorioCompartidoAPI } from '../services/api';
import { pageImageSrc } from '../utils/staticUrl';
import { buildPartituraDownloadFilename } from '../utils/partituraDownloadFilename';

const STORAGE_PREFIX = 'cadencya_repertorio_share_v1_';
const VISTA_KEY = 'cadencya_repertorio_public_vista';

const VISOR_ZOOM_MIN = 0.35;
const VISOR_ZOOM_MAX = 3;
const PAGE_GAP = 16;
const EMPTY_VISOR_PAGINAS = [];

function clampVisorZoom(z) {
  return Math.max(VISOR_ZOOM_MIN, Math.min(VISOR_ZOOM_MAX, z));
}

/** Clave estable por página en el documento continuo (evita colisiones entre obras). */
function visorPageKey(p, index) {
  return `${p?.obraId ?? 'obra'}:${p?.id ?? 'p'}:${index}`;
}

function rememberImgNatural(img, pageKey, setNaturals) {
  if (!img || !pageKey) return;
  const nw = img.naturalWidth;
  const nh = img.naturalHeight;
  if (!nw || !nh) return;
  setNaturals((prevNat) => {
    const cur = prevNat[pageKey];
    if (cur?.nw === nw && cur?.nh === nh) return prevNat;
    return { ...prevNat, [pageKey]: { nw, nh } };
  });
}

/** Desktop con ratón real (sin táctil). En iPhone/iPad siempre false. */
function isFinePointerDesktop() {
  if (typeof window === 'undefined') return false;
  // Cualquier dispositivo táctil (iPhone, iPad, Android) → no es “desktop ratón”
  if ((navigator.maxTouchPoints || 0) > 0) return false;
  if ('ontouchstart' in window) return false;
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch {
    return false;
  }
}

/** Visor inmersivo en cualquier táctil o viewport estrecho. */
function detectMobileVisor() {
  if (typeof window === 'undefined') return true;
  if (!isFinePointerDesktop()) return true;
  try {
    if (window.matchMedia('(max-width: 1023px)').matches) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** Oculta el chrome hasta un toque. */
function visorChromeIsShown(visible) {
  return Boolean(visible || isFinePointerDesktop());
}

/** Tamaño del rectángulo que envuelve la imagen tras `rotate(rotDeg)` (grados). */
function rotatedBoundingCssSize(width, height, rotDeg) {
  const r = (((Number(rotDeg) || 0) % 360) + 360) % 360;
  if (r === 90 || r === 270) return { w: height, h: width };
  return { w: width, h: height };
}

function storageKey(token, musicaLinkToken = '') {
  const m = (musicaLinkToken || '').trim();
  return `${STORAGE_PREFIX}${encodeURIComponent(token)}_${m ? encodeURIComponent(m) : '0'}`;
}

function storageSessionKey(token, musicaLinkToken = '') {
  return `${storageKey(token, musicaLinkToken)}__session`;
}

/** Restaura paso main + músico tras recarga / descarte de pestaña en móvil. */
function readShareSession(token, musicaLinkToken = '') {
  try {
    const raw = sessionStorage.getItem(storageSessionKey(token, musicaLinkToken));
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object' || !data.musicoId) return null;
    return data;
  } catch {
    return null;
  }
}

function writeShareSession(token, musicaLinkToken, payload) {
  try {
    sessionStorage.setItem(
      storageSessionKey(token, musicaLinkToken),
      JSON.stringify(payload),
    );
  } catch {
    /* ignore quota / private mode */
  }
}

function clearShareSession(token, musicaLinkToken = '') {
  try {
    sessionStorage.removeItem(storageSessionKey(token, musicaLinkToken));
  } catch {
    /* ignore */
  }
}

export default function RepertorioCompartidoPublic() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const musicaLinkToken = (searchParams.get('m') || '').trim();
  
  const bg = useColorModeValue('gray.50', 'gray.900');
  const headerBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.200', 'gray.600');
  const sidebarBg = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.800');

  const [splashDone, setSplashDone] = useState(false);
  const [gateLoading, setGateLoading] = useState(false);
  const [gateError, setGateError] = useState(null);
  const [meta, setMeta] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  /** null hasta terminar el splash; luego password | musician | main */
  const [step, setStep] = useState(null);
  const [pwd, setPwd] = useState('');
  const [pwdSubmitting, setPwdSubmitting] = useState(false);

  const [musicos, setMusicos] = useState([]);
  const [selectedMusico, setSelectedMusico] = useState(null);
  const [musicianSubmitting, setMusicianSubmitting] = useState(false);
  const [musicianPickView, setMusicianPickView] = useState('list'); // 'list' | 'confirm'
  const musicosList = Array.isArray(musicos) ? musicos : [];

  const [carpetas, setCarpetas] = useState([]);
  const [selectedCarpetaId, setSelectedCarpetaId] = useState(null);
  const [obras, setObras] = useState([]);
  const [obrasLoading, setObrasLoading] = useState(false);
  const [carpetasConteoObras, setCarpetasConteoObras] = useState({});
  const [carpetasConteoLoading, setCarpetasConteoLoading] = useState(false);

  const [visorDetail, setVisorDetail] = useState(null);
  const [visorPageIndex, setVisorPageIndex] = useState(0);
  const [visorZoom, setVisorZoom] = useState(1);
  /** Controles del visor: en móvil se ocultan/muestran al tocar. */
  const [visorChromeVisible, setVisorChromeVisible] = useState(true);
  const [isMobileVisor, setIsMobileVisor] = useState(() => detectMobileVisor());
  /** Dimensiones naturales por id de página (documento continuo tipo PDF). */
  const [visorImgNaturals, setVisorImgNaturals] = useState(
    /** @type {Record<string, { nw: number, nh: number }>} */ ({}),
  );
  /** Área útil del contenedor con scroll (solo viewport; el zoom no debe recalcular `nw`/`nh`). */
  const [visorViewBox, setVisorViewBox] = useState({ cw: 0, ch: 0 });
  /** Tras girar el móvil: no pintar tamaño fijo en px hasta estabilizar (evita zoom de Safari). */
  /** Tras girar: solo re-medir. No tocar docReady (evita spinner eterno). */
  const [visorSettling, setVisorSettling] = useState(false);
  /**
   * Hasta que el scroll inicial a la obra esté garantizado (como un PDF que acaba de abrir).
   * Mientras tanto: spinner a pantalla completa y gestos desactivados.
   */
  const [visorDocReady, setVisorDocReady] = useState(false);
  /** Remonta el contenedor de scroll en cada apertura (reenganche fiable de gestos en iOS/Android). */
  const [visorSessionKey, setVisorSessionKey] = useState(0);
  /** Tamaño del documento a zoom 1 (para el spacer del scale global). */
  const [visorContentSize, setVisorContentSize] = useState({ w: 0, h: 0 });
  const visorScrollRef = useRef(null);
  const visorContentRef = useRef(null);
  /** Índice de página al que hacer scroll al abrir la obra. */
  const visorPendingScrollIndexRef = useRef(0);
  const visorDidInitialScrollRef = useRef(false);
  /** Corrección única de scroll cuando la página objetivo ya tiene tamaño real. */
  const visorDidLayoutCorrectRef = useRef(false);
  const visorProgrammaticScrollRef = useRef(false);
  const visorDocReadyRef = useRef(false);
  /** Página a reanclar tras girar / re-medir viewport (evita saltar a otra página). */
  const visorPinPageOnRelayoutRef = useRef(/** @type {number | null} */ (null));
  const visorPageElsRef = useRef(/** @type {Map<string, HTMLElement>} */ (new Map()));
  const visorPinchRef = useRef({
    active: false,
    startDist: 0,
    startZoom: 1,
  });
  const visorDragRef = useRef({
    down: false,
    startX: 0,
    startY: 0,
    startScrollLeft: 0,
    startScrollTop: 0,
  });
  /** Toque (mostrar/ocultar chrome); ya no cambia de página. */
  const visorTapRef = useRef({
    tracking: false,
    startX: 0,
    startY: 0,
    moved: false,
  });
  const [visorLoading, setVisorLoading] = useState(false);
  const [carpetaPdfLoadingId, setCarpetaPdfLoadingId] = useState(null);
  const [carpetaZipLoadingId, setCarpetaZipLoadingId] = useState(null);
  const [obraPdfLoadingId, setObraPdfLoadingId] = useState(null);
  const { open: notasModalOpen, onOpen: onNotasModalOpen, onClose: onNotasModalClose } = useDisclosure();
  const descargaCarpetaModal = useDisclosure();
  const {
    open: listadoObrasModalOpen,
    onOpen: onListadoObrasModalOpen,
    onClose: onListadoObrasModalClose,
  } = useDisclosure();
  const {
    open: incidenciaModalOpen,
    onOpen: onIncidenciaModalOpen,
    onClose: onIncidenciaModalClose,
  } = useDisclosure();
  const {
    open: perfilMusicoModalOpen,
    onOpen: onPerfilMusicoModalOpen,
    onClose: onPerfilMusicoModalClose,
  } = useDisclosure();
  const [listadoObrasReferencia, setListadoObrasReferencia] = useState([]);
  const [listadoObrasLoading, setListadoObrasLoading] = useState(false);
  const [descargaCarpetaModo, setDescargaCarpetaModo] = useState('pdf'); // 'pdf' | 'zip'

  /** Slug de instrumento (catálogo) para filtrar como en partichellas. */
  const [partituraInstrumentSlug, setPartituraInstrumentSlug] = useState(null);
  /** Slugs de voz separados por coma; misma lógica que el PDF con filtro de voces. */
  const [partituraVozEtiquetasCsv, setPartituraVozEtiquetasCsv] = useState(null);
  /** Id de fila MusicoInstrumento cuando hay varias asignaciones (valor del desplegable). */
  const [selectedAsignacionId, setSelectedAsignacionId] = useState('');
  const [vista, setVista] = useState(() => {
    try {
      const v = localStorage.getItem(VISTA_KEY);
      return v === 'mosaic' ? 'mosaic' : 'list';
    } catch {
      return 'list';
    }
  });

  const persistVista = (v) => {
    setVista(v);
    try {
      localStorage.setItem(VISTA_KEY, v);
    } catch {
      /* ignore */
    }
  };

  const asignaciones = selectedMusico?.asignaciones || [];
  const needsInstrumentChoice = asignaciones.length > 1;

  const partituraInstrumentLabel = useMemo(() => {
    if (!partituraInstrumentSlug) return null;
    const selected = asignaciones.find((a) => a.id === selectedAsignacionId);
    if (selected?.displayName) return selected.displayName;
    if (asignaciones.length === 1 && asignaciones[0]?.displayName) return asignaciones[0].displayName;
    return partituraInstrumentSlug;
  }, [partituraInstrumentSlug, asignaciones, selectedAsignacionId]);

  const downloadCarpetaPdf = useCallback(
    async (carpetaId, carpetaNombre) => {
      if (!token || !accessToken) return;
      if (needsInstrumentChoice && !selectedAsignacionId) {
        toaster.create({
          title: 'Elige tu instrumento',
          description: 'Así el PDF incluirá solo las páginas de tu voz.',
          type: 'warning',
        });
        return;
      }
      try {
        setCarpetaPdfLoadingId(carpetaId);
        const todas = asignaciones.length === 0 || !partituraInstrumentSlug;
        const params = todas
          ? { todasLasPaginas: 'true' }
          : {
              instrumentEtiqueta: partituraInstrumentSlug,
              ...(partituraVozEtiquetasCsv ? { vozEtiquetas: partituraVozEtiquetasCsv } : {}),
            };
        const res = await repertorioCompartidoAPI.getCarpetaPdfBlob(token, accessToken, carpetaId, params);
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildPartituraDownloadFilename({
          primary: carpetaNombre || 'carpeta',
          instrumentLabel: partituraInstrumentLabel,
          todas,
          ext: 'pdf',
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toaster.create({ title: 'PDF descargado', type: 'success' });
      } catch {
        toaster.create({
          title: 'No se pudo generar el PDF',
          description: 'Comprueba la conexión o inténtalo más tarde.',
          type: 'error',
        });
      } finally {
        setCarpetaPdfLoadingId(null);
      }
    },
    [
      token,
      accessToken,
      needsInstrumentChoice,
      selectedAsignacionId,
      partituraInstrumentSlug,
      partituraInstrumentLabel,
      partituraVozEtiquetasCsv,
      asignaciones.length,
    ],
  );

  const downloadCarpetaZip = useCallback(
    async (carpetaId, carpetaNombre) => {
      if (!token || !accessToken) return;
      if (needsInstrumentChoice && !selectedAsignacionId) {
        toaster.create({
          title: 'Elige tu instrumento',
          description: 'Así el ZIP incluirá solo las páginas de tu voz.',
          type: 'warning',
        });
        return;
      }
      try {
        setCarpetaZipLoadingId(carpetaId);
        const todas = asignaciones.length === 0 || !partituraInstrumentSlug;
        const params = todas
          ? { todasLasPaginas: 'true' }
          : {
              instrumentEtiqueta: partituraInstrumentSlug,
              ...(partituraVozEtiquetasCsv ? { vozEtiquetas: partituraVozEtiquetasCsv } : {}),
            };
        const res = await repertorioCompartidoAPI.getCarpetaZipBlob(token, accessToken, carpetaId, params);
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildPartituraDownloadFilename({
          primary: carpetaNombre || 'carpeta',
          instrumentLabel: partituraInstrumentLabel,
          todas,
          ext: 'zip',
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toaster.create({ title: 'ZIP descargado', type: 'success' });
      } catch {
        toaster.create({
          title: 'No se pudo generar el ZIP',
          description: 'Comprueba la conexión o inténtalo más tarde.',
          type: 'error',
        });
      } finally {
        setCarpetaZipLoadingId(null);
      }
    },
    [
      token,
      accessToken,
      needsInstrumentChoice,
      selectedAsignacionId,
      partituraInstrumentSlug,
      partituraInstrumentLabel,
      partituraVozEtiquetasCsv,
      asignaciones.length,
    ],
  );

  const downloadObraPdf = useCallback(
    async (obraId, obraTitulo) => {
      if (!token || !accessToken) return;
      if (needsInstrumentChoice && !selectedAsignacionId) {
        toaster.create({
          title: 'Elige tu instrumento',
          description: 'Así el PDF incluirá solo las páginas de tu voz.',
          type: 'warning',
        });
        return;
      }
      try {
        setObraPdfLoadingId(obraId);
        const todas = asignaciones.length === 0 || !partituraInstrumentSlug;
        const params = todas
          ? { todasLasPaginas: 'true' }
          : {
              instrumentEtiqueta: partituraInstrumentSlug,
              ...(partituraVozEtiquetasCsv ? { vozEtiquetas: partituraVozEtiquetasCsv } : {}),
            };
        const res = await repertorioCompartidoAPI.getObraPdfBlob(token, accessToken, obraId, params);
        const blob = res.data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = buildPartituraDownloadFilename({
          primary: obraTitulo || 'obra',
          instrumentLabel: partituraInstrumentLabel,
          todas,
          ext: 'pdf',
        });
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toaster.create({ title: 'PDF descargado', type: 'success' });
      } catch {
        toaster.create({
          title: 'No se pudo generar el PDF',
          description: 'Comprueba la conexión o inténtalo más tarde.',
          type: 'error',
        });
      } finally {
        setObraPdfLoadingId(null);
      }
    },
    [
      token,
      accessToken,
      needsInstrumentChoice,
      selectedAsignacionId,
      partituraInstrumentSlug,
      partituraInstrumentLabel,
      partituraVozEtiquetasCsv,
      asignaciones.length,
    ],
  );

  const enterMainWithMusico = useCallback(
    async (musico, bearerToken, restore = {}) => {
      if (!token || !bearerToken || !musico) throw new Error('Sesión incompleta');
      const cRes = await repertorioCompartidoAPI.getCarpetas(token, bearerToken);
      const list = cRes.data || [];
      setCarpetas(list);
      const preferredCarpeta =
        restore.carpetaId && list.some((c) => String(c.id) === String(restore.carpetaId))
          ? restore.carpetaId
          : list[0]?.id || null;
      setSelectedCarpetaId(preferredCarpeta);

      const asig = musico.asignaciones || [];
      const restoredAsignacionId = restore.selectedAsignacionId || restore.asignacionId || '';
      const picked =
        restoredAsignacionId && asig.find((a) => String(a.id) === String(restoredAsignacionId));
      if (picked) {
        setSelectedAsignacionId(picked.id || '');
        setPartituraInstrumentSlug(
          restore.partituraInstrumentSlug || picked.instrumentSlug || picked.etiqueta || null,
        );
        setPartituraVozEtiquetasCsv(
          restore.partituraVozEtiquetasCsv ??
            (Array.isArray(picked.vozEtiquetas) && picked.vozEtiquetas.length
              ? picked.vozEtiquetas.join(',')
              : null),
        );
      } else if (asig.length === 1) {
        const a = asig[0];
        setSelectedAsignacionId(a.id || '');
        setPartituraInstrumentSlug(a.instrumentSlug || a.etiqueta || null);
        setPartituraVozEtiquetasCsv(
          Array.isArray(a.vozEtiquetas) && a.vozEtiquetas.length ? a.vozEtiquetas.join(',') : null,
        );
      } else {
        setSelectedAsignacionId('');
        setPartituraInstrumentSlug(null);
        setPartituraVozEtiquetasCsv(null);
      }
      setSelectedMusico(musico);
      setStep('main');
    },
    [token, musicaLinkToken],
  );

  const runGate = useCallback(async () => {
    if (!token) return;
    setGateLoading(true);
    setGateError(null);
    const key = storageKey(token, musicaLinkToken);
    const unlockExtras = musicaLinkToken ? { musicoLinkToken: musicaLinkToken } : {};
    try {
      const metaRes = await repertorioCompartidoAPI.meta(token);
      setMeta(metaRes.data);
      const stored = sessionStorage.getItem(key);
      if (stored) {
        try {
          const mRes = await repertorioCompartidoAPI.getMusicos(token, stored);
          const list = Array.isArray(mRes.data) ? mRes.data : [];
          setMusicos(list);
          setAccessToken(stored);
          const saved = readShareSession(token, musicaLinkToken);
          if (saved?.musicoId) {
            const row = list.find((x) => String(x.id) === String(saved.musicoId));
            if (row) {
              try {
                await enterMainWithMusico(row, stored, saved);
                return;
              } catch {
                clearShareSession(token, musicaLinkToken);
              }
            }
          }
          setStep('musician');
          return;
        } catch {
          sessionStorage.removeItem(key);
          clearShareSession(token, musicaLinkToken);
        }
      }
      if (metaRes.data?.requiresPassword) {
        setStep('password');
        return;
      }
      const unlockRes = await repertorioCompartidoAPI.unlock(token, unlockExtras);
      const at = unlockRes.data?.accessToken;
      if (!at) throw new Error('Respuesta inválida');
      sessionStorage.setItem(key, at);
      setAccessToken(at);
      const mRes = await repertorioCompartidoAPI.getMusicos(token, at);
      const list = Array.isArray(mRes.data) ? mRes.data : [];
      setMusicos(list);
      const pre = unlockRes.data?.musicoPreseleccionado;
      if (pre?.id) {
        const row = list.find((x) => x.id === pre.id);
        if (row) {
          setSelectedMusico(row);
          try {
            await enterMainWithMusico(row, at);
          } catch {
            toaster.create({ title: 'Error al cargar carpetas', type: 'error' });
            setStep('musician');
          }
          return;
        }
      }
      setStep('musician');
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        'No se pudo cargar el enlace. Comprueba que el enlace sea correcto.';
      setGateError(typeof msg === 'string' ? msg : 'Error al cargar');
    } finally {
      setGateLoading(false);
    }
  }, [token, musicaLinkToken, enterMainWithMusico]);

  const gateRanRef = useRef(false);
  const onSplashComplete = useCallback(() => setSplashDone(true), []);

  useEffect(() => {
    if (!splashDone) return;
    // Una sola pasada por montaje: si runGate se re-ejecutara con token en sessionStorage,
    // antes forzaba otra vez la pantalla de músicos (muy visible en móvil tras un rato).
    if (gateRanRef.current) return;
    gateRanRef.current = true;
    runGate();
  }, [splashDone, runGate]);

  /** Mantener sesión de main (músico/carpeta/instrumento) para restaurar tras recarga. */
  useEffect(() => {
    if (step !== 'main' || !token || !accessToken || !selectedMusico?.id) return;
    writeShareSession(token, musicaLinkToken, {
      musicoId: selectedMusico.id,
      carpetaId: selectedCarpetaId,
      selectedAsignacionId,
      partituraInstrumentSlug,
      partituraVozEtiquetasCsv,
    });
  }, [
    step,
    token,
    musicaLinkToken,
    accessToken,
    selectedMusico?.id,
    selectedCarpetaId,
    selectedAsignacionId,
    partituraInstrumentSlug,
    partituraVozEtiquetasCsv,
  ]);

  const submitPassword = async () => {
    if (!token || !pwd || pwd.length < 8) {
      toaster.create({ title: 'La contraseña debe tener al menos 8 caracteres', type: 'warning' });
      return;
    }
    const key = storageKey(token, musicaLinkToken);
    const unlockExtras = musicaLinkToken ? { musicoLinkToken: musicaLinkToken } : {};
    try {
      setPwdSubmitting(true);
      const unlockRes = await repertorioCompartidoAPI.unlock(token, { password: pwd, ...unlockExtras });
      const at = unlockRes.data?.accessToken;
      if (!at) throw new Error('Respuesta inválida');
      sessionStorage.setItem(key, at);
      setAccessToken(at);
      const mRes = await repertorioCompartidoAPI.getMusicos(token, at);
      const list = Array.isArray(mRes.data) ? mRes.data : [];
      setMusicos(list);
      setPwd('');
      const pre = unlockRes.data?.musicoPreseleccionado;
      if (pre?.id) {
        const row = list.find((x) => x.id === pre.id);
        if (row) {
          setSelectedMusico(row);
          try {
            setMusicianSubmitting(true);
            await enterMainWithMusico(row, at);
          } catch {
            toaster.create({ title: 'Error al cargar carpetas', type: 'error' });
            setStep('musician');
          } finally {
            setMusicianSubmitting(false);
          }
          return;
        }
      }
      setStep('musician');
    } catch (e) {
      const msg = e?.response?.data?.message;
      toaster.create({
        title: 'No se pudo acceder',
        description: typeof msg === 'string' ? msg : 'Contraseña incorrecta.',
        type: 'error',
      });
    } finally {
      setPwdSubmitting(false);
    }
  };

  const confirmMusician = async () => {
    if (!selectedMusico) {
      toaster.create({ title: 'Selecciona un músico', type: 'warning' });
      return;
    }
    if (!accessToken) {
      toaster.create({ title: 'Sesión no disponible', type: 'error' });
      return;
    }
    try {
      setMusicianSubmitting(true);
      await enterMainWithMusico(selectedMusico, accessToken);
    } catch (e) {
      toaster.create({ title: 'Error al cargar carpetas', type: 'error' });
    } finally {
      setMusicianSubmitting(false);
    }
  };

  /** Vuelve a la elección de músico sin pedir de nuevo la contraseña del enlace. */
  const cerrarSesionMusico = useCallback(() => {
    if (token) clearShareSession(token, musicaLinkToken);
    onPerfilMusicoModalClose();
    setVisorDetail(null);
    setVisorPageIndex(0);
    setVisorZoom(1);
    setVisorImgNaturals({});
    setVisorViewBox({ cw: 0, ch: 0 });
    setVisorDocReady(false);
    visorDocReadyRef.current = false;
    setSelectedMusico(null);
    setSelectedAsignacionId('');
    setPartituraInstrumentSlug(null);
    setPartituraVozEtiquetasCsv(null);
    setCarpetas([]);
    setSelectedCarpetaId(null);
    setObras([]);
    setMusicianPickView('list');
    setStep('musician');
  }, [token, musicaLinkToken, onPerfilMusicoModalClose]);

  // Al entrar en el paso de músico, arrancamos siempre en listado.
  useEffect(() => {
    if (step === 'musician') setMusicianPickView('list');
  }, [step]);

  useEffect(() => {
    if (step !== 'main' || !token || !accessToken || !selectedCarpetaId) return;
    if (needsInstrumentChoice && !selectedAsignacionId) {
      setObras([]);
      setObrasLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setObrasLoading(true);
        const params = {};
        if (partituraInstrumentSlug) params.instrumentEtiqueta = partituraInstrumentSlug;
        if (partituraVozEtiquetasCsv) params.vozEtiquetas = partituraVozEtiquetasCsv;
        const res = await repertorioCompartidoAPI.getObrasEnCarpeta(
          token,
          accessToken,
          selectedCarpetaId,
          params,
        );
        if (!cancelled) setObras(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled) setObras([]);
        toaster.create({ title: 'Error al cargar obras', type: 'error' });
      } finally {
        if (!cancelled) setObrasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    step,
    token,
    accessToken,
    selectedCarpetaId,
    partituraInstrumentSlug,
    partituraVozEtiquetasCsv,
    selectedAsignacionId,
    needsInstrumentChoice,
  ]);

  const carpetasConteoKey = useMemo(() => {
    const i = partituraInstrumentSlug || 'ALL';
    const v = partituraVozEtiquetasCsv || '';
    return `${i}__${v}`;
  }, [partituraInstrumentSlug, partituraVozEtiquetasCsv]);

  useEffect(() => {
    if (step !== 'main' || !token || !accessToken) return;
    if (!Array.isArray(carpetas) || carpetas.length === 0) return;
    if (needsInstrumentChoice && !selectedAsignacionId) return;
    let cancelled = false;
    (async () => {
      try {
        setCarpetasConteoLoading(true);
        const params = {};
        if (partituraInstrumentSlug) params.instrumentEtiqueta = partituraInstrumentSlug;
        if (partituraVozEtiquetasCsv) params.vozEtiquetas = partituraVozEtiquetasCsv;
        const results = await Promise.allSettled(
          carpetas.map(async (c) => {
            const res = await repertorioCompartidoAPI.getObrasEnCarpeta(
              token,
              accessToken,
              c.id,
              params,
            );
            const list = Array.isArray(res.data) ? res.data : [];
            return [c.id, list.length];
          }),
        );
        if (cancelled) return;
        setCarpetasConteoObras((prev) => {
          const next = { ...prev };
          const baseKey = carpetasConteoKey;
          for (const r of results) {
            if (r.status !== 'fulfilled') continue;
            const [carpetaId, count] = r.value || [];
            if (!carpetaId) continue;
            next[`${baseKey}:${carpetaId}`] = Number.isFinite(count) ? count : 0;
          }
          return next;
        });
      } catch {
        // ignore: el conteo es informativo
      } finally {
        if (!cancelled) setCarpetasConteoLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    step,
    token,
    accessToken,
    carpetas,
    needsInstrumentChoice,
    selectedAsignacionId,
    partituraInstrumentSlug,
    partituraVozEtiquetasCsv,
    carpetasConteoKey,
  ]);

  const selectedCarpeta = useMemo(
    () => carpetas.find((c) => c.id === selectedCarpetaId) || null,
    [carpetas, selectedCarpetaId],
  );

  const abrirListadoObrasCarpeta = useCallback(async () => {
    if (!token || !accessToken || !selectedCarpetaId) return;
    onListadoObrasModalOpen();
    setListadoObrasLoading(true);
    setListadoObrasReferencia([]);
    try {
      const res = await repertorioCompartidoAPI.getObrasEnCarpeta(token, accessToken, selectedCarpetaId, {});
      setListadoObrasReferencia(Array.isArray(res.data) ? res.data : []);
    } catch {
      toaster.create({
        title: 'No se pudo cargar el listado',
        description: 'Inténtalo de nuevo en unos instantes.',
        type: 'error',
      });
      onListadoObrasModalClose();
    } finally {
      setListadoObrasLoading(false);
    }
  }, [token, accessToken, selectedCarpetaId, onListadoObrasModalOpen, onListadoObrasModalClose]);

  const instrumentSelectValue = selectedAsignacionId || '';

  const visorPaginas = visorDetail?.paginas || EMPTY_VISOR_PAGINAS;
  const visorPaginaActual = visorPaginas[visorPageIndex] || null;
  const visorPageIndexRef = useRef(visorPageIndex);
  visorPageIndexRef.current = visorPageIndex;
  const visorZoomRef = useRef(visorZoom);
  visorZoomRef.current = visorZoom;
  const visorImgNaturalsRef = useRef(visorImgNaturals);
  visorImgNaturalsRef.current = visorImgNaturals;
  const visorViewBoxRef = useRef(visorViewBox);
  visorViewBoxRef.current = visorViewBox;
  /** Evita que el IntersectionObserver pise el scroll inicial a la obra abierta. */
  const visorAllowIoRef = useRef(false);

  /** Evitar zoom raro de Safari en el listado (móvil). */
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (!meta) return undefined;
    const prev = meta.getAttribute('content');
    const locked =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    meta.setAttribute('content', locked);
    try {
      window.scrollTo(0, 0);
      if ('zoom' in document.documentElement.style) {
        document.documentElement.style.zoom = '1';
        document.body.style.zoom = '1';
      }
    } catch {
      /* ignore */
    }
    return () => {
      if (prev != null) meta.setAttribute('content', prev);
    };
  }, []);

  /** Al abrir otra obra: reset de zoom y métricas (no al hacer scroll entre páginas). */
  useEffect(() => {
    if (!visorDetail?.id) return;
    setVisorZoom(1);
    setVisorImgNaturals({});
    setVisorViewBox({ cw: 0, ch: 0 });
    visorAllowIoRef.current = false;
    setVisorDocReady(false);
    visorDocReadyRef.current = false;
  }, [visorDetail?.id, visorSessionKey]);

  /**
   * Refs del modal a veces no existen en el primer commit; reintentamos con rAF.
   * Al girar (sobre todo a horizontal): Safari hace zoom de página si el contenido
   * desborda un instante; ese zoom se queda al volver a vertical. Por eso:
   * 1) vaciamos el tamaño fijo en px, 2) reseteamos el viewport meta, 3) re-medimos.
   */
  useEffect(() => {
    if (!visorDetail?.id) return undefined;

    let cancelled = false;
    let rafId = 0;
    let retries = 0;
    const maxRetries = 24;
    const detachRef = { current: () => {} };
    /** @type {ReturnType<typeof setTimeout>[]} */
    const pendingTimers = [];
    let orientationResizeHandler = null;
    /** @type {ResizeObserver | null} */
    let ro = null;

    const VISOR_VIEWPORT_LOCKED =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';

    const resetPageScaleHard = () => {
      try {
        const meta = document.querySelector('meta[name="viewport"]');
        if (meta) {
          meta.setAttribute(
            'content',
            'width=device-width, initial-scale=0.99, maximum-scale=1, user-scalable=no, viewport-fit=cover',
          );
          pendingTimers.push(
            setTimeout(() => {
              meta.setAttribute('content', VISOR_VIEWPORT_LOCKED);
            }, 16),
          );
        }
        window.scrollTo(0, 0);
        if ('zoom' in document.documentElement.style) {
          document.documentElement.style.zoom = '1';
          document.body.style.zoom = '1';
        }
      } catch {
        /* ignore */
      }
    };

    const trySetup = () => {
      if (cancelled) return;
      const sc = visorScrollRef.current;
      if (!sc) {
        retries += 1;
        if (retries <= maxRetries) {
          rafId = requestAnimationFrame(trySetup);
        }
        return;
      }

      const measureViewport = () => {
        if (cancelled) return;
        const pad = 4;
        const node = visorScrollRef.current || sc;
        const cw = Math.max(32, (node.clientWidth || 0) - pad * 2);
        const ch = Math.max(32, (node.clientHeight || 0) - pad * 2);
        if (cw < 40 || ch < 40) return;
        setVisorViewBox((prev) => {
          if (Math.abs(prev.cw - cw) < 2 && Math.abs(prev.ch - ch) < 2) return prev;
          // El scrollbar clásico (Windows) resta ~12–17px de clientWidth y, si
          // reajustamos el fit, el contenido cabe y el scrollbar desaparece: bucle.
          if (
            prev.cw > 0 &&
            Math.abs(prev.ch - ch) < 2 &&
            Math.abs(prev.cw - cw) <= 20
          ) {
            return prev;
          }
          // Cambio notable de área: fijar página actual antes de que el layout mueva el scroll.
          if (
            visorDocReadyRef.current &&
            (Math.abs(prev.cw - cw) > 8 || Math.abs(prev.ch - ch) > 8)
          ) {
            if (visorPinPageOnRelayoutRef.current == null) {
              visorPinPageOnRelayoutRef.current = visorPageIndexRef.current;
            }
            visorAllowIoRef.current = false;
          }
          return { cw, ch };
        });
      };

      const restorePinnedPage = () => {
        const idx =
          visorPinPageOnRelayoutRef.current != null
            ? visorPinPageOnRelayoutRef.current
            : visorPageIndexRef.current;
        if (idx == null || idx < 0) return;
        const pages = visorDetail?.paginas || [];
        const p = pages[idx];
        if (!p) return;
        const key = visorPageKey(p, idx);
        const el = visorPageElsRef.current.get(key) || visorPageElsRef.current.get(p.id);
        const scrollEl = visorScrollRef.current || sc;
        if (!el || !scrollEl) return;
        const scRect = scrollEl.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        visorProgrammaticScrollRef.current = true;
        scrollEl.scrollTop += elRect.top - scRect.top;
        scrollEl.scrollLeft += elRect.left - scRect.left;
        requestAnimationFrame(() => {
          visorProgrammaticScrollRef.current = false;
        });
      };

      const finishOrientationSettle = () => {
        if (cancelled) return;
        resetPageScaleHard();
        measureViewport();
        pendingTimers.push(
          setTimeout(() => {
            if (cancelled) return;
            measureViewport();
            resetPageScaleHard();
            // Reanclar varias veces: el layout/scale aún puede moverse un frame después.
            restorePinnedPage();
            requestAnimationFrame(() => {
              restorePinnedPage();
              pendingTimers.push(
                setTimeout(() => {
                  if (cancelled) return;
                  restorePinnedPage();
                  const pinned = visorPinPageOnRelayoutRef.current;
                  if (pinned != null) {
                    setVisorPageIndex(pinned);
                    visorPageIndexRef.current = pinned;
                  }
                  visorPinPageOnRelayoutRef.current = null;
                  setVisorSettling(false);
                  setVisorDocReady(true);
                  visorDocReadyRef.current = true;
                  visorAllowIoRef.current = true;
                }, 80),
              );
            });
          }, 120),
        );
      };

      const onWindowResize = () => {
        measureViewport();
      };

      const onOrientationChange = () => {
        // Guardar página visible YA (antes de que zoom/layout muevan el scroll).
        visorPinPageOnRelayoutRef.current = visorPageIndexRef.current;
        visorAllowIoRef.current = false;
        setVisorZoom(1);
        if (!isFinePointerDesktop()) setVisorChromeVisible(true);
        setVisorSettling(true);
        resetPageScaleHard();
        measureViewport();

        if (orientationResizeHandler) {
          window.removeEventListener('resize', orientationResizeHandler);
        }
        let done = false;
        orientationResizeHandler = () => {
          if (done || cancelled) return;
          done = true;
          window.removeEventListener('resize', orientationResizeHandler);
          orientationResizeHandler = null;
          finishOrientationSettle();
        };
        window.addEventListener('resize', orientationResizeHandler);
        pendingTimers.push(
          setTimeout(() => {
            if (!done && !cancelled) {
              done = true;
              if (orientationResizeHandler) {
                window.removeEventListener('resize', orientationResizeHandler);
                orientationResizeHandler = null;
              }
              finishOrientationSettle();
            }
          }, 500),
        );
        [50, 200, 450].forEach((ms) => {
          pendingTimers.push(
            setTimeout(() => {
              resetPageScaleHard();
              measureViewport();
              restorePinnedPage();
            }, ms),
          );
        });
      };

      const onWheelZoom = (e) => {
        if (!e.ctrlKey && !e.metaKey) return;
        const delta = e.deltaY;
        if (!delta) return;
        e.preventDefault();
        e.stopPropagation();
        const factor = Math.exp(-delta * 0.0012);
        setVisorZoom((z) => clampVisorZoom(z * factor));
      };

      measureViewport();
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => measureViewport());
        ro.observe(sc);
      }
      window.addEventListener('resize', onWindowResize);
      window.addEventListener('orientationchange', onOrientationChange);
      const so = window.screen?.orientation;
      if (so && typeof so.addEventListener === 'function') {
        so.addEventListener('change', onOrientationChange);
      }

      sc.addEventListener('wheel', onWheelZoom, { passive: false });

      detachRef.current = () => {
        ro?.disconnect();
        ro = null;
        window.removeEventListener('resize', onWindowResize);
        window.removeEventListener('orientationchange', onOrientationChange);
        if (so && typeof so.removeEventListener === 'function') {
          so.removeEventListener('change', onOrientationChange);
        }
        if (orientationResizeHandler) {
          window.removeEventListener('resize', orientationResizeHandler);
        }
        sc.removeEventListener('wheel', onWheelZoom);
        pendingTimers.forEach(clearTimeout);
      };
    };

    rafId = requestAnimationFrame(trySetup);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      detachRef.current();
    };
  }, [visorDetail?.id, visorSessionKey]);

  /**
   * Gestos PDF (pellizco + pan): efecto propio, reenganchado en cada sesión del visor.
   * Capture + listeners nativos; no mezclar con el efecto de medida/orientación.
   */
  useEffect(() => {
    if (!visorDetail?.id) return undefined;

    let cancelled = false;
    let rafId = 0;
    let attached = null;
    /** @type {ReturnType<typeof setTimeout>[]} */
    const timers = [];

    const resetGestureState = () => {
      visorPinchRef.current = {
        active: false,
        startDist: 0,
        startZoom: visorZoomRef.current,
      };
      visorDragRef.current.down = false;
      visorTapRef.current.tracking = false;
      visorTapRef.current.moved = false;
    };

    const attach = () => {
      if (cancelled) return;
      const sc = visorScrollRef.current;
      if (!sc) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      if (attached?.sc === sc) return;
      resetGestureState();

      const onTouchStart = (e) => {
        if (!visorDocReadyRef.current) return;
        if (e.target?.closest?.('[data-visor-chrome]')) return;
        if (e.touches?.length >= 2) {
          const [a, b] = e.touches;
          const dx = a.clientX - b.clientX;
          const dy = a.clientY - b.clientY;
          visorPinchRef.current = {
            active: true,
            startDist: Math.hypot(dx, dy) || 1,
            startZoom: visorZoomRef.current,
          };
          visorTapRef.current.tracking = false;
          visorDragRef.current.down = false;
          return;
        }
        if (e.touches?.length === 1) {
          const t = e.touches[0];
          visorTapRef.current = {
            tracking: true,
            startX: t.clientX,
            startY: t.clientY,
            moved: false,
          };
          // Pan manual siempre con 1 dedo (touch-action:none; scroll nativo no aplica).
          // Solo tras doc ready, para no pelear con el scroll inicial a la obra.
          visorDragRef.current = {
            down: true,
            startX: t.clientX,
            startY: t.clientY,
            startScrollLeft: sc.scrollLeft,
            startScrollTop: sc.scrollTop,
          };
        }
      };

      const onTouchMove = (e) => {
        if (!visorDocReadyRef.current) return;
        if (e.touches?.length >= 2) {
          // Si el 2.º dedo llega sin touchstart de 2, inicia pellizco aquí.
          if (!visorPinchRef.current.active) {
            const [a, b] = e.touches;
            const dx = a.clientX - b.clientX;
            const dy = a.clientY - b.clientY;
            visorPinchRef.current = {
              active: true,
              startDist: Math.hypot(dx, dy) || 1,
              startZoom: visorZoomRef.current,
            };
            visorDragRef.current.down = false;
          }
          e.preventDefault();
          const [a, b] = e.touches;
          const dx = a.clientX - b.clientX;
          const dy = a.clientY - b.clientY;
          const dist = Math.hypot(dx, dy);
          const ratio = visorPinchRef.current.startDist
            ? dist / visorPinchRef.current.startDist
            : 1;
          const prevZ = visorZoomRef.current;
          const nextZ = clampVisorZoom(visorPinchRef.current.startZoom * ratio);
          if (nextZ !== prevZ && prevZ > 0) {
            const cx = sc.scrollLeft + sc.clientWidth / 2;
            const cy = sc.scrollTop + sc.clientHeight / 2;
            const factor = nextZ / prevZ;
            setVisorZoom(nextZ);
            // Mantener el centro del viewport al escalar todo el documento.
            requestAnimationFrame(() => {
              sc.scrollLeft = cx * factor - sc.clientWidth / 2;
              sc.scrollTop = cy * factor - sc.clientHeight / 2;
            });
          }
          visorTapRef.current.moved = true;
          return;
        }
        if (e.touches?.length === 1 && visorDragRef.current.down) {
          e.preventDefault();
          const t = e.touches[0];
          const dx = t.clientX - visorDragRef.current.startX;
          const dy = t.clientY - visorDragRef.current.startY;
          if (Math.abs(dx) > 8 || Math.abs(dy) > 8) visorTapRef.current.moved = true;
          sc.scrollLeft = visorDragRef.current.startScrollLeft - dx;
          sc.scrollTop = visorDragRef.current.startScrollTop - dy;
        }
      };

      const onTouchEnd = (e) => {
        if (e.target?.closest?.('[data-visor-chrome]')) {
          resetGestureState();
          return;
        }
        const tap = visorTapRef.current;
        const wasPinch = visorPinchRef.current.active;
        if (wasPinch && e.touches?.length === 1) {
          const t = e.touches[0];
          visorPinchRef.current = {
            active: false,
            startDist: 0,
            startZoom: visorZoomRef.current,
          };
          visorDragRef.current = {
            down: true,
            startX: t.clientX,
            startY: t.clientY,
            startScrollLeft: sc.scrollLeft,
            startScrollTop: sc.scrollTop,
          };
          visorTapRef.current.moved = true;
          return;
        }
        visorPinchRef.current = {
          active: false,
          startDist: 0,
          startZoom: visorZoomRef.current,
        };
        visorDragRef.current.down = false;
        if (
          visorDocReadyRef.current &&
          !wasPinch &&
          tap.tracking &&
          !tap.moved &&
          !isFinePointerDesktop()
        ) {
          setVisorChromeVisible((v) => !v);
        }
        visorTapRef.current.moved = true;
        visorTapRef.current.tracking = false;
        timers.push(
          setTimeout(() => {
            visorTapRef.current.moved = false;
          }, 350),
        );
      };

      const detachGestures = () => {
        sc.removeEventListener('touchstart', onTouchStart, true);
        sc.removeEventListener('touchmove', onTouchMove, true);
        sc.removeEventListener('touchend', onTouchEnd, true);
        sc.removeEventListener('touchcancel', onTouchEnd, true);
      };

      sc.addEventListener('touchstart', onTouchStart, { passive: true, capture: true });
      sc.addEventListener('touchmove', onTouchMove, { passive: false, capture: true });
      sc.addEventListener('touchend', onTouchEnd, { passive: true, capture: true });
      sc.addEventListener('touchcancel', onTouchEnd, { passive: true, capture: true });

      attached = { sc, detach: detachGestures };
    };

    rafId = requestAnimationFrame(attach);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      timers.forEach(clearTimeout);
      resetGestureState();
      attached?.detach?.();
    };
  }, [visorDetail?.id, visorSessionKey]);

  visorDocReadyRef.current = visorDocReady;

  const visorPageLayouts = useMemo(() => {
    const { cw, ch } = visorViewBox;
    /** @type {Record<string, { rot: number, nw: number, nh: number, cw: number, ch: number, fit: number, dw?: number, dh?: number, bbox: { w: number, h: number } | null, key: string }>} */
    const layouts = {};
    visorPaginas.forEach((p, index) => {
      const key = visorPageKey(p, index);
      const rot = Number(p.rotacion) || 0;
      const nat = visorImgNaturals[key] || visorImgNaturals[p.id] || { nw: 0, nh: 0 };
      const { nw, nh } = nat;
      if (!nw || !nh || !cw || !ch) {
        layouts[key] = { rot, nw, nh, cw, ch, fit: 1, dw: undefined, dh: undefined, bbox: null, key };
        return;
      }
      const eff = rotatedBoundingCssSize(nw, nh, rot);
      if (!eff.w || !eff.h) {
        layouts[key] = { rot, nw, nh, cw, ch, fit: 1, dw: undefined, dh: undefined, bbox: null, key };
        return;
      }
      // Fit a viewport a zoom 1; el zoom del documento es un scale CSS global.
      const fit = Math.min(cw / eff.w, ch / eff.h);
      const safeFit = Number.isFinite(fit) && fit > 0 ? fit : 1;
      const dw = nw * safeFit;
      const dh = nh * safeFit;
      const bbox = rotatedBoundingCssSize(dw, dh, rot);
      layouts[key] = { rot, nw, nh, cw, ch, fit: safeFit, dw, dh, bbox, key };
    });
    return layouts;
  }, [visorPaginas, visorImgNaturals, visorViewBox]);

  useLayoutEffect(() => {
    if (!visorDetail?.id) {
      setVisorContentSize((prevSize) =>
        prevSize.w === 0 && prevSize.h === 0 ? prevSize : { w: 0, h: 0 },
      );
      return;
    }
    const el = visorContentRef.current;
    const wrapW = visorViewBox.cw;
    if (!el || !wrapW) return;
    const prevTransform = el.style.transform;
    const prevWidth = el.style.width;
    const prevMaxWidth = el.style.maxWidth;
    // Medir a zoom 1 y con el ancho de viewport, no el del spacer escalado.
    el.style.transform = 'none';
    el.style.width = `${wrapW}px`;
    el.style.maxWidth = 'none';
    const w = Math.round(el.scrollWidth || el.offsetWidth || 0);
    const h = Math.round(el.scrollHeight || el.offsetHeight || 0);
    el.style.transform = prevTransform;
    el.style.width = prevWidth;
    el.style.maxWidth = prevMaxWidth;
    if (w > 0 && h > 0) {
      setVisorContentSize((prevSize) => {
        if (Math.abs(prevSize.w - w) <= 2 && Math.abs(prevSize.h - h) <= 2) {
          return prevSize;
        }
        return { w, h };
      });
    }
  }, [
    visorDetail?.id,
    visorSessionKey,
    visorPaginas,
    visorViewBox.cw,
    visorViewBox.ch,
    visorDocReady,
    visorImgNaturals,
  ]);

  const scrollVisorToPageIndex = useCallback(
    (idx) => {
      const p = visorPaginas[idx];
      if (!p) return false;
      const key = visorPageKey(p, idx);
      const el = visorPageElsRef.current.get(key) || visorPageElsRef.current.get(p.id);
      const sc = visorScrollRef.current;
      if (!el || !sc) return false;
      const scRect = sc.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      // getBoundingClientRect ya incluye el scale del documento.
      visorProgrammaticScrollRef.current = true;
      sc.scrollTop += elRect.top - scRect.top;
      sc.scrollLeft += elRect.left - scRect.left;
      requestAnimationFrame(() => {
        visorProgrammaticScrollRef.current = false;
      });
      return true;
    },
    [visorPaginas],
  );

  /** Tras girar/re-medir: volver a la misma página (el scroll en px ya no coincide). */
  useLayoutEffect(() => {
    if (!visorDetail?.id) return undefined;
    const pinned = visorPinPageOnRelayoutRef.current;
    if (pinned == null) return undefined;
    scrollVisorToPageIndex(pinned);
    // Si no hay settle de orientación en curso, liberar el pin tras reanclar.
    if (visorSettling) return undefined;
    const t = setTimeout(() => {
      if (visorPinPageOnRelayoutRef.current !== pinned) return;
      setVisorPageIndex(pinned);
      visorPinPageOnRelayoutRef.current = null;
      visorAllowIoRef.current = true;
    }, 100);
    return () => clearTimeout(t);
  }, [
    visorDetail?.id,
    visorViewBox,
    visorPageLayouts,
    visorZoom,
    visorContentSize,
    visorSettling,
    scrollVisorToPageIndex,
  ]);

  /**
   * Scroll inicial a la obra abierta.
   * Re-ajusta mientras cargan imágenes anteriores (cambian alturas); para al tener
   * tamaño real en 0..idx, al timeout, o si el usuario hace scroll a mano.
   * No depende de orientation settling (si no, el spinner se queda pillado al girar).
   */
  useEffect(() => {
    if (!visorDetail?.id) return undefined;
    const idx = visorPendingScrollIndexRef.current;
    if (!visorPaginas[idx]) return undefined;

    let cancelled = false;
    let rafId = 0;
    /** @type {ReturnType<typeof setTimeout> | null} */
    let enableIoTimer = null;

    const pagesReadyThrough = (throughIdx) => {
      const { cw, ch } = visorViewBoxRef.current;
      if (!cw || !ch) return false;
      const naturals = visorImgNaturalsRef.current;
      for (let i = 0; i <= throughIdx; i += 1) {
        const p = visorPaginas[i];
        if (!p) return false;
        const key = visorPageKey(p, i);
        if (!visorPageElsRef.current.get(key) && !visorPageElsRef.current.get(p.id)) return false;
        const nat = naturals[key] || naturals[p.id];
        if (!nat?.nw || !nat?.nh) return false;
      }
      return true;
    };

    const onUserScroll = () => {
      if (visorProgrammaticScrollRef.current) return;
      if (!visorAllowIoRef.current) return;
      visorDidLayoutCorrectRef.current = true;
    };

    const finishAndEnableIo = () => {
      if (cancelled) return;
      setVisorPageIndex(idx);
      visorDidInitialScrollRef.current = true;
      setVisorDocReady(true);
      visorDocReadyRef.current = true;
      if (enableIoTimer) clearTimeout(enableIoTimer);
      enableIoTimer = setTimeout(() => {
        if (!cancelled) visorAllowIoRef.current = true;
      }, 80);
    };

    const tick = () => {
      if (cancelled) return;
      if (visorDidLayoutCorrectRef.current && visorDocReadyRef.current) return;
      const elReady = Boolean(
        visorPaginas[idx] &&
          (visorPageElsRef.current.get(visorPageKey(visorPaginas[idx], idx)) ||
            visorPageElsRef.current.get(visorPaginas[idx].id)),
      );
      if (elReady) {
        scrollVisorToPageIndex(idx);
        if (!visorDidInitialScrollRef.current) {
          setVisorPageIndex(idx);
          visorDidInitialScrollRef.current = true;
        }
      }
      if (pagesReadyThrough(idx)) {
        scrollVisorToPageIndex(idx);
        finishAndEnableIo();
        visorDidLayoutCorrectRef.current = true;
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    visorAllowIoRef.current = false;
    visorDidInitialScrollRef.current = false;
    visorDidLayoutCorrectRef.current = false;
    setVisorDocReady(false);
    visorDocReadyRef.current = false;

    let scrollRoot = /** @type {HTMLElement | null} */ (null);
    const ensureScrollListener = () => {
      if (cancelled) return;
      scrollRoot = visorScrollRef.current;
      if (!scrollRoot) {
        rafId = requestAnimationFrame(ensureScrollListener);
        return;
      }
      scrollRoot.addEventListener('scroll', onUserScroll, { passive: true });
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(ensureScrollListener);

    const giveUpTimer = setTimeout(() => {
      if (cancelled) return;
      scrollVisorToPageIndex(idx);
      finishAndEnableIo();
      visorDidLayoutCorrectRef.current = true;
    }, 6000);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (enableIoTimer) clearTimeout(enableIoTimer);
      clearTimeout(giveUpTimer);
      scrollRoot?.removeEventListener('scroll', onUserScroll);
    };
  }, [visorDetail?.id, visorPaginas, visorSessionKey, scrollVisorToPageIndex]);

  useEffect(() => {
    if (!visorDetail?.id) {
      visorDidLayoutCorrectRef.current = false;
    }
  }, [visorDetail?.id]);

  /** Sincronizar índice de página con scroll (documento continuo). */
  useEffect(() => {
    if (!visorDetail?.id || visorPaginas.length === 0) return undefined;

    const idToIndex = new Map(visorPaginas.map((p, i) => [visorPageKey(p, i), i]));
    let cancelled = false;
    let observer = null;
    let rafId = 0;

    const attach = () => {
      if (cancelled) return;
      const root = visorScrollRef.current;
      if (!root) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      const els = visorPaginas
        .map((p, i) => visorPageElsRef.current.get(visorPageKey(p, i)))
        .filter(Boolean);
      if (!els.length) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          if (!visorAllowIoRef.current) return;
          if (visorPinPageOnRelayoutRef.current != null) return;
          let bestRatio = 0;
          let bestIndex = -1;
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const id = entry.target.getAttribute('data-visor-page-id');
            const idx = idToIndex.get(id);
            if (idx == null) continue;
            if (entry.intersectionRatio > bestRatio) {
              bestRatio = entry.intersectionRatio;
              bestIndex = idx;
            }
          }
          if (bestIndex >= 0) {
            setVisorPageIndex((prev) => (prev === bestIndex ? prev : bestIndex));
          }
        },
        { root, threshold: [0, 0.15, 0.35, 0.55, 0.75, 1] },
      );
      for (const el of els) observer.observe(el);
    };

    rafId = requestAnimationFrame(attach);

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      observer?.disconnect();
    };
  }, [visorDetail?.id, visorPaginas, visorPageLayouts, visorSessionKey]);

  const cerrarVisor = useCallback(() => {
    setVisorDetail(null);
    setVisorPageIndex(0);
    setVisorZoom(1);
    setVisorImgNaturals({});
    setVisorViewBox({ cw: 0, ch: 0 });
    setVisorSettling(false);
    setVisorChromeVisible(false);
    setVisorDocReady(false);
    visorDocReadyRef.current = false;
    visorPageElsRef.current.clear();
    visorDidInitialScrollRef.current = false;
    visorDidLayoutCorrectRef.current = false;
    visorAllowIoRef.current = false;
    visorPinchRef.current = { active: false, startDist: 0, startZoom: 1 };
    visorDragRef.current.down = false;
    visorPinPageOnRelayoutRef.current = null;
    onNotasModalClose();
  }, [onNotasModalClose]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const apply = () => setIsMobileVisor(detectMobileVisor());
    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
    };
  }, []);

  useEffect(() => {
    if (!visorDetail) return;
    // Al abrir: controles visibles; en móvil un toque los oculta / muestra.
    setVisorChromeVisible(true);
  }, [visorDetail?.id]);

  useEffect(() => {
    if (!visorDetail) return undefined;
    const prev = {
      overflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow,
      touchAction: document.body.style.touchAction,
      overscroll: document.body.style.overscrollBehavior,
      htmlOverscroll: document.documentElement.style.overscrollBehavior,
    };
    const meta = document.querySelector('meta[name="viewport"]');

    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';

    // Bloquear zoom de página del navegador mientras el visor está abierto
    // (el pellizco del visor es nuestro; sin esto Safari escala toda la web al girar).
    const VISOR_VIEWPORT_LOCKED =
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
    if (meta) {
      meta.setAttribute('content', VISOR_VIEWPORT_LOCKED);
    }

    const blockBrowserZoom = (e) => {
      if (!e.touches || e.touches.length < 2) return;
      // Dentro del visor lo gestiona nuestro pellizco; no interferir en capture.
      const sc = visorScrollRef.current;
      if (sc && (sc === e.target || sc.contains(/** @type {Node} */ (e.target)))) return;
      e.preventDefault();
    };
    const blockGesture = (e) => e.preventDefault();
    document.addEventListener('touchmove', blockBrowserZoom, { passive: false, capture: true });
    document.addEventListener('gesturestart', blockGesture, { passive: false, capture: true });
    document.addEventListener('gesturechange', blockGesture, { passive: false, capture: true });

    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.overflow;
      document.body.style.touchAction = prev.touchAction;
      document.body.style.overscrollBehavior = prev.overscroll;
      document.documentElement.style.overscrollBehavior = prev.htmlOverscroll;
      // Volver al listado siempre a escala 1 (evitar zoom raro en móvil).
      const LIST_VIEWPORT =
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover';
      if (meta) {
        meta.setAttribute('content', LIST_VIEWPORT);
      }
      try {
        window.scrollTo(0, 0);
      } catch {
        /* ignore */
      }
      if ('zoom' in document.documentElement.style) {
        document.documentElement.style.zoom = '1';
        document.body.style.zoom = '1';
      }
      document.removeEventListener('touchmove', blockBrowserZoom, { capture: true });
      document.removeEventListener('gesturestart', blockGesture, { capture: true });
      document.removeEventListener('gesturechange', blockGesture, { capture: true });
    };
  }, [visorDetail]);

  const notasBloques = useMemo(() => {
    if (!visorPaginas.length) return [];
    const out = [];
    const seenObra = new Set();
    for (const p of visorPaginas) {
      const obraKey = p.obraId || '_';
      if (!seenObra.has(obraKey)) {
        seenObra.add(obraKey);
        const obraNotas = p.obraNotasDirector?.trim();
        if (obraNotas) {
          out.push({
            key: `obra-${obraKey}`,
            titulo: p.obraTitulo?.trim() || 'Sobre la obra',
            texto: obraNotas,
          });
        }
      }
      if (p.notasDirector?.trim()) {
        out.push({
          key: p.id,
          titulo: `${p.obraTitulo?.trim() || 'Obra'} · orden ${p.orden}`,
          texto: p.notasDirector.trim(),
        });
      }
    }
    return out;
  }, [visorPaginas]);

  const hayAlgunaNota = notasBloques.length > 0;
  const notaEnPaginaActual = !!visorPaginaActual?.notasDirector?.trim();
  const visorTituloActual =
    visorPaginaActual?.obraTitulo?.trim() || visorDetail?.titulo || '';
  const visorAutorActual =
    visorPaginaActual?.obraAutor?.trim() || visorDetail?.autor || '';

  const visorZoomOut = useCallback(() => {
    setVisorZoom((z) => clampVisorZoom(z / 1.08));
  }, []);

  const visorZoomIn = useCallback(() => {
    setVisorZoom((z) => clampVisorZoom(z * 1.08));
  }, []);

  const abrirObra = useCallback(
    async (obraId, options = {}) => {
      const { startAtLastPage = false, startAtPageIndex } = options;
      if (!token || !accessToken) return false;
      const lista = Array.isArray(obras) ? obras : [];
      if (!lista.length) {
        toaster.create({ title: 'No hay obras en esta carpeta', type: 'info' });
        return false;
      }
      try {
        setVisorLoading(true);
        const params = {};
        if (!(needsInstrumentChoice && !selectedAsignacionId)) {
          if (partituraInstrumentSlug) params.instrumentEtiqueta = partituraInstrumentSlug;
          if (partituraVozEtiquetasCsv) params.vozEtiquetas = partituraVozEtiquetasCsv;
        }

        // Documento continuo = todas las obras de la carpeta (como un PDF de la carpeta).
        const detalles = await Promise.all(
          lista.map(async (o) => {
            try {
              const res = await repertorioCompartidoAPI.getObraDetalle(
                token,
                accessToken,
                o.id,
                params,
              );
              return res.data;
            } catch {
              return null;
            }
          }),
        );

        const flatPages = [];
        for (let oi = 0; oi < lista.length; oi += 1) {
          const data = detalles[oi];
          const meta = lista[oi];
          if (!data?.paginas?.length) continue;
          const titulo = data.titulo || meta?.titulo || 'Sin título';
          const autor = data.autor || meta?.autor || '';
          const obraNotas = data.notasDirector || null;
          for (const p of data.paginas) {
            flatPages.push({
              ...p,
              obraId: data.id || meta?.id,
              obraTitulo: titulo,
              obraAutor: autor,
              obraNotasDirector: obraNotas,
            });
          }
        }

        if (!flatPages.length) {
          toaster.create({
            title: 'Esta carpeta no tiene páginas para tu instrumento',
            type: 'info',
          });
          return false;
        }

        const n = flatPages.length;
        let idx = 0;
        if (startAtPageIndex != null && Number.isFinite(Number(startAtPageIndex))) {
          idx = Math.max(0, Math.min(n - 1, Math.floor(Number(startAtPageIndex))));
        } else if (startAtLastPage) {
          idx = Math.max(0, n - 1);
        } else {
          const target = String(obraId);
          const firstOfObra = flatPages.findIndex((p) => String(p.obraId) === target);
          idx = firstOfObra >= 0 ? firstOfObra : 0;
        }

        const carpetaNombre = selectedCarpeta?.nombre?.trim() || 'Carpeta';
        const opened =
          detalles.find((d) => d && String(d.id) === String(obraId)) || detalles.find(Boolean);

        visorPendingScrollIndexRef.current = idx;
        visorDidInitialScrollRef.current = false;
        visorAllowIoRef.current = false;
        visorDidLayoutCorrectRef.current = false;
        visorPageElsRef.current.clear();
        setVisorDocReady(false);
        visorDocReadyRef.current = false;
        setVisorSessionKey((k) => k + 1);
        setVisorZoom(1);
        setVisorImgNaturals({});
        setVisorViewBox({ cw: 0, ch: 0 });
        setVisorSettling(false);
        setVisorChromeVisible(true);
        setVisorPageIndex(idx);
        setVisorDetail({
          id: `carpeta:${selectedCarpetaId || 'doc'}`,
          titulo: opened?.titulo || carpetaNombre,
          autor: opened?.autor || null,
          notasDirector: null,
          paginas: flatPages,
          carpetaId: selectedCarpetaId,
          carpetaNombre,
        });
        return true;
      } catch {
        toaster.create({ title: 'No se pudo abrir la carpeta', type: 'error' });
        return false;
      } finally {
        setVisorLoading(false);
      }
    },
    [
      token,
      accessToken,
      obras,
      needsInstrumentChoice,
      selectedAsignacionId,
      partituraInstrumentSlug,
      partituraVozEtiquetasCsv,
      selectedCarpeta,
      selectedCarpetaId,
    ],
  );

  const visorIrAnterior = useCallback(() => {
    if (visorPageIndex <= 0) return false;
    const next = visorPageIndex - 1;
    setVisorPageIndex(next);
    scrollVisorToPageIndex(next);
    return true;
  }, [visorPageIndex, scrollVisorToPageIndex]);

  const visorIrSiguiente = useCallback(() => {
    if (visorPageIndex >= visorPaginas.length - 1) return false;
    const next = visorPageIndex + 1;
    setVisorPageIndex(next);
    scrollVisorToPageIndex(next);
    return true;
  }, [visorPageIndex, visorPaginas.length, scrollVisorToPageIndex]);

  useEffect(() => {
    if (!visorDetail) return undefined;
    const onKey = (e) => {
      if (notasModalOpen) {
        if (e.key === 'Escape') {
          e.preventDefault();
          onNotasModalClose();
        }
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cerrarVisor();
        return;
      }
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        visorIrAnterior();
      }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        visorIrSiguiente();
      }
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        visorZoomIn();
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        visorZoomOut();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    visorDetail,
    notasModalOpen,
    cerrarVisor,
    onNotasModalClose,
    visorIrAnterior,
    visorIrSiguiente,
    visorZoomIn,
    visorZoomOut,
  ]);

  if (!token) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={bg} px={4}>
        <Text>Enlace no válido</Text>
      </Flex>
    );
  }

  if (gateError && splashDone) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={bg} px={4}>
        <Card.Root maxW="md" w="full" shadow="lg" rounded="2xl">
          <Card.Body>
            <Heading size="md" mb={2}>
              No disponible
            </Heading>
            <Text color="gray.600">{gateError}</Text>
          </Card.Body>
        </Card.Root>
      </Flex>
    );
  }

  return (
    <>
      <SEO
        title={meta?.bandaNombre ? `Repertorio · ${meta.bandaNombre}` : 'Repertorio compartido'}
        description={
          meta?.bandaNombre
            ? `Consulta el repertorio de partituras de ${meta.bandaNombre} en Cadencya.`
            : 'Consulta el repertorio de partituras compartido en Cadencya.'
        }
        path={typeof window !== 'undefined' ? window.location.pathname : ''}
        noIndex
      />
      {!splashDone && (
        <LoadingScreen
          message="Cargando repertorio compartido…"
          duration={2000}
          onComplete={onSplashComplete}
        />
      )}

      {splashDone && gateLoading && (
        <Flex minH="100vh" align="center" justify="center" bg={bg}>
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
        </Flex>
      )}

      <Dialog.Root
        open={step === 'password'}
        closeOnInteractOutside={false}
        placement='center'
        size='md'
        onOpenChange={e => {
          if (!e.open)
            {}
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Contraseña del enlace</Dialog.Header>
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={4}>
                  Esta banda ha protegido el repertorio. Introduce la contraseña que te hayan facilitado.
                </Text>
                <Field.Root>
                  <Field.Label fontSize="sm">Contraseña</Field.Label>
                  <Input
                    type="password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoComplete="off"
                    placeholder="Mínimo 8 caracteres"
                  />
                </Field.Root>
              </Dialog.Body>
              <Dialog.Footer gap={2}>
                <Button colorPalette="purple" onClick={submitPassword} loading={pwdSubmitting}>
                  Continuar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={step === 'musician'}
        closeOnInteractOutside={false}
        placement='center'
        size='lg'
        onOpenChange={e => {
          if (!e.open)
            {}
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>¿Quién eres en la formación?</Dialog.Header>
              <Dialog.Body>
                {musicianPickView === 'confirm' && selectedMusico ? (
                  <VStack gap={4} py={6} textAlign="center">
                    <Avatar.Root size="lg"><Avatar.Fallback
                        name={[selectedMusico.nombre, selectedMusico.apellidos].filter(Boolean).join(' ')} /><Avatar.Image src={selectedMusico.foto || undefined} /></Avatar.Root>
                    <Heading size="md">
                      Hola{' '}
                      {[selectedMusico.nombre, selectedMusico.apellidos].filter(Boolean).join(' ')}
                    </Heading>
                    <Text fontSize="sm" color="gray.600">
                      Pulsa Continuar para ver el repertorio, o Atrás si te has equivocado.
                    </Text>
                  </VStack>
                ) : (
                  <>
                    <Text fontSize="sm" color="gray.600" mb={4}>
                      Selecciona tu nombre en la lista. Si tienes varios instrumentos asignados, en la siguiente pantalla
                      elegirás cuál estás usando para filtrar las partituras.
                    </Text>
                    <VStack
                      align="stretch"
                      gap={2}
                      // Más alto por defecto para que no quede “enana” si hay pocos músicos
                      minH={{ base: '52vh', md: '56vh' }}
                      maxH={{ base: '70vh', md: '72vh' }}
                      overflowY="auto"
                    >
                      {musicosList.length === 0 ? (
                        <Text fontSize="sm" color="gray.600">
                          No hay músicos activos configurados en esta banda. Contacta con la banda.
                        </Text>
                      ) : (
                        musicosList.map((m) => {
                          const label = [m.nombre, m.apellidos].filter(Boolean).join(' ');
                          const instrumentosTxt =
                            Array.isArray(m.asignaciones) && m.asignaciones.length
                              ? m.asignaciones.map((a) => a.displayName).filter(Boolean).join(' · ')
                              : '';
                          return (
                            <Button
                              key={m.id}
                              variant="outline"
                              colorPalette="gray"
                              justifyContent="flex-start"
                              h="auto"
                              py={3}
                              px={4}
                              onClick={() => {
                                setSelectedMusico(m);
                                setMusicianPickView('confirm');
                              }}
                            >
                              <HStack gap={3} w="full">
                                <Avatar.Root size="sm" flexShrink={0}><Avatar.Fallback name={label} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                                <Box flex="1" minW={0}>
                                  <Text textAlign="left" fontWeight="700" lineClamp={1}>
                                    {label}
                                  </Text>
                                  {instrumentosTxt ? (
                                    <Text textAlign="left" fontSize="xs" opacity={0.9} lineClamp={2}>
                                      {instrumentosTxt}
                                    </Text>
                                  ) : (
                                    <Text textAlign="left" fontSize="xs" opacity={0.7}>
                                      Sin instrumentos asignados
                                    </Text>
                                  )}
                                </Box>
                              </HStack>
                            </Button>
                          );
                        })
                      )}
                    </VStack>
                  </>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                {musicianPickView === 'confirm' ? (
                  <HStack w="full" justify="space-between">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setMusicianPickView('list');
                        setSelectedMusico(null);
                      }}
                      disabled={musicianSubmitting}
                    >
                      Atrás
                    </Button>
                    <Button
                      colorPalette="purple"
                      onClick={confirmMusician}
                      loading={musicianSubmitting}
                      disabled={!selectedMusico || musicos.length === 0}
                    >
                      Continuar
                    </Button>
                  </HStack>
                ) : (
                  <Button variant="outline" disabled>
                    Selecciona un músico…
                  </Button>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={perfilMusicoModalOpen}
        onOpenChange={(e) => {
          if (!e.open) onPerfilMusicoModalClose();
        }}
        placement="center"
        size="md"
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Tu perfil</Dialog.Header>
              <Dialog.Body>
                {selectedMusico ? (
                  <VStack align="stretch" gap={4}>
                    <HStack gap={4} align="center">
                      <Avatar.Root size="lg">
                        <Avatar.Fallback
                          name={[selectedMusico.nombre, selectedMusico.apellidos]
                            .filter(Boolean)
                            .join(' ')}
                        />
                        <Avatar.Image src={selectedMusico.foto || undefined} />
                      </Avatar.Root>
                      <Box minW={0}>
                        <Heading size="md" lineClamp={2}>
                          {[selectedMusico.nombre, selectedMusico.apellidos]
                            .filter(Boolean)
                            .join(' ') || 'Músico'}
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                          {meta?.bandaNombre || 'Banda'}
                        </Text>
                      </Box>
                    </HStack>

                    <Separator />

                    <VStack align="stretch" gap={3}>
                      <Box>
                        <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                          Nombre
                        </Text>
                        <Text fontSize="sm">{selectedMusico.nombre || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase">
                          Apellidos
                        </Text>
                        <Text fontSize="sm">{selectedMusico.apellidos?.trim() || '—'}</Text>
                      </Box>
                      <Box>
                        <Text
                          fontSize="xs"
                          fontWeight="700"
                          color="gray.500"
                          textTransform="uppercase"
                          mb={1}
                        >
                          Instrumentos y voces
                        </Text>
                        {Array.isArray(selectedMusico.asignaciones) &&
                        selectedMusico.asignaciones.length > 0 ? (
                          <VStack align="stretch" gap={1}>
                            {selectedMusico.asignaciones.map((a) => (
                              <Text key={a.id} fontSize="sm">
                                {a.displayName ||
                                  [a.instrumentoNombre, a.vozNombre].filter(Boolean).join(' · ') ||
                                  '—'}
                              </Text>
                            ))}
                          </VStack>
                        ) : (
                          <Text fontSize="sm" color="gray.600">
                            Sin instrumentos asignados
                          </Text>
                        )}
                      </Box>
                    </VStack>
                  </VStack>
                ) : (
                  <Text fontSize="sm" color="gray.600">
                    No hay datos de músico seleccionados.
                  </Text>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="outline" onClick={onPerfilMusicoModalClose}>
                  Cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {step === 'main' ? (
        <Flex
          direction="column"
          minH="100vh"
          bg={bg}
          overflowX="hidden"
          maxW="100%"
          aria-hidden={visorDetail ? 'true' : undefined}
          visibility={visorDetail ? 'hidden' : 'visible'}
          pointerEvents={visorDetail ? 'none' : 'auto'}
        >
          <Box
            as="header"
            borderBottomWidth="1px"
            borderColor={borderCol}
            bg={headerBg}
            py={3}
            px={{ base: 4, md: 8 }}
          >
            <Container maxW="7xl">
              <HStack gap={4} justify="space-between" align="center">
                <HStack gap={4} minW={0} flex="1">
                  <Image src="/logo y texto cadencya.png" alt="Cadencya" h="40px" w="auto" flexShrink={0} />
                  <Separator orientation="vertical" h="32px" display={{ base: 'none', sm: 'block' }} />
                  <VStack align="start" gap={0} minW={0}>
                    <Text fontSize="xs" color="gray.500" fontWeight="600" textTransform="uppercase">
                      Repertorio compartido
                    </Text>
                    <Heading size="sm" isTruncated maxW={{ base: '140px', sm: '200px', md: '100%' }}>
                      {meta?.bandaNombre || 'Banda'}
                    </Heading>
                  </VStack>
                </HStack>

                {selectedMusico ? (
                  <Menu.Root>
                    <Menu.Trigger
                      aria-label="Menú de perfil"
                      borderRadius="full"
                      flexShrink={0}
                      cursor="pointer"
                      _focusVisible={{ boxShadow: 'outline' }}
                    >
                      <Avatar.Root size="sm">
                        <Avatar.Fallback
                          name={[selectedMusico.nombre, selectedMusico.apellidos]
                            .filter(Boolean)
                            .join(' ')}
                        />
                        <Avatar.Image src={selectedMusico.foto || undefined} />
                      </Avatar.Root>
                    </Menu.Trigger>
                    <Portal>
                      <Menu.Positioner>
                        <Menu.Content minW="200px">
                          <Menu.Item
                            value="detalles"
                            onSelect={() => onPerfilMusicoModalOpen()}
                          >
                            <HStack gap={2}>
                              <Icon as={FaUser} />
                              <Text>Detalles</Text>
                            </HStack>
                          </Menu.Item>
                          <Menu.Separator />
                          <Menu.Item
                            value="cerrar-sesion"
                            color="red.500"
                            onSelect={() => cerrarSesionMusico()}
                          >
                            <HStack gap={2}>
                              <Icon as={FaSignOutAlt} />
                              <Text>Cerrar sesión</Text>
                            </HStack>
                          </Menu.Item>
                        </Menu.Content>
                      </Menu.Positioner>
                    </Portal>
                  </Menu.Root>
                ) : null}
              </HStack>
            </Container>
          </Box>

          <Box flex="1" py={6} px={{ base: 4, md: 6 }}>
            <Container maxW="7xl">
              <VStack align="stretch" gap={4} mb={6}>
                {selectedMusico ? (
                  <HStack flexWrap="wrap" gap={3} align="center">
                    <Text fontSize="sm" color="gray.600">
                      Músico:
                    </Text>
                    <Text fontWeight="600" fontSize="sm">
                      {[selectedMusico.nombre, selectedMusico.apellidos].filter(Boolean).join(' ')}
                    </Text>
                  </HStack>
                ) : null}
                {asignaciones.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    No hay instrumentos asignados en tu ficha: se muestran todas las partituras listas de cada carpeta.
                  </Text>
                ) : asignaciones.length === 1 ? (
                  <Text fontSize="sm" color="gray.600">
                    Instrumento: <strong>{asignaciones[0].displayName}</strong>
                  </Text>
                ) : (
                  <Field.Root maxW="md">
                    <Field.Label fontSize="sm" mb={1}>
                      Selecciona tu instrumento
                    </Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        value={instrumentSelectValue}
                        onChange={(e) => {
                          const id = e.target.value;
                          setSelectedAsignacionId(id);
                          const a = asignaciones.find((x) => x.id === id);
                          if (a) {
                            setPartituraInstrumentSlug(a.instrumentSlug || a.etiqueta || null);
                            setPartituraVozEtiquetasCsv(
                              Array.isArray(a.vozEtiquetas) && a.vozEtiquetas.length
                                ? a.vozEtiquetas.join(',')
                                : null,
                            );
                          } else {
                            setPartituraInstrumentSlug(null);
                            setPartituraVozEtiquetasCsv(null);
                          }
                        }}>
                        <option value="">— Elige —</option>
                        {asignaciones.map((a) => (
                          <option key={a.id} value={a.id}>
                            {a.displayName}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                  </Field.Root>
                )}
              </VStack>

              <Flex
                gap={6}
                align="flex-start"
                direction={{ base: 'column', md: 'row' }}
                minH={{ md: '480px' }}
              >
                <Box
                  w={{ base: 'full', md: 'min(320px, 34vw)' }}
                  minW={{ md: '300px' }}
                  flexShrink={0}
                  bg={sidebarBg}
                  borderWidth="1px"
                  borderColor={borderCol}
                  rounded="xl"
                  p={3}
                >
                  <HStack mb={2} gap={2}>
                    <Icon asChild><FaFolder /></Icon>
                    <Text fontWeight="700" fontSize="sm">
                      Carpetas
                    </Text>
                  </HStack>
                  <VStack align="stretch" gap={1}>
                    {carpetas.length === 0 ? (
                      <Text fontSize="sm" color="gray.500">
                        No hay carpetas.
                      </Text>
                    ) : (
                      carpetas.map((c) => {
                        const count =
                          carpetasConteoObras[`${carpetasConteoKey}:${c.id}`] ??
                          (carpetasConteoLoading ? null : undefined);
                        const label =
                          count === null || typeof count === 'undefined'
                            ? c.nombre
                            : `${c.nombre} (${count})`;
                        return (
                          <Button
                            key={c.id}
                            size="sm"
                            variant={c.id === selectedCarpetaId ? 'solid' : 'ghost'}
                            colorPalette="purple"
                            justifyContent="flex-start"
                            fontWeight={c.id === selectedCarpetaId ? '700' : '500'}
                            onClick={() => setSelectedCarpetaId(c.id)}
                            w="full"
                            minW={0}
                            px={2}
                            py={2}
                            h="auto"
                            minH="32px"
                            whiteSpace="normal"
                            textAlign="left"
                            alignItems="flex-start"
                          >
                            <Text
                              textAlign="left"
                              w="full"
                              lineClamp={2}
                              lineHeight="short"
                              wordBreak="break-word"
                            >
                              {label}
                            </Text>
                          </Button>
                        );
                      })
                    )}
                  </VStack>
                </Box>

                <Box flex="1" minW={0}>
                  <HStack justify="space-between" mb={4} flexWrap="wrap" gap={3} align="flex-start">
                    <VStack align="start" gap={2} flex={1} minW={0}>
                      <HStack gap={3} flexWrap="wrap" align="center" w="full">
                        <Heading size="md" isTruncated maxW={{ base: 'full', md: 'min(320px, 50vw)' }}>
                          {selectedCarpeta?.nombre || 'Partituras'}
                        </Heading>
                        {selectedCarpetaId ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="purple"
                              flexShrink={0}
                              onClick={() => void abrirListadoObrasCarpeta()}
                              loading={listadoObrasLoading}
                              disabled={!accessToken}><FaClipboardList />Ver listado de obras
                                                          </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="orange"
                              flexShrink={0}
                              onClick={onIncidenciaModalOpen}
                              disabled={!accessToken}><FaExclamationTriangle />Reportar incidencia
                                                          </Button>
                          </>
                        ) : null}
                      </HStack>
                      {!obrasLoading && Array.isArray(obras) ? (
                        <Text fontSize="sm" color="gray.600">
                          {obras.length} {obras.length === 1 ? 'obra' : 'obras'}
                        </Text>
                      ) : null}
                      {selectedCarpetaId ? (
                        <Button
                          size="sm"
                          variant="outline"
                          colorPalette="purple"
                          onClick={() => {
                            setDescargaCarpetaModo('pdf');
                            descargaCarpetaModal.onOpen();
                          }}
                          loading={
                            carpetaPdfLoadingId === selectedCarpetaId ||
                            carpetaZipLoadingId === selectedCarpetaId
                          }
                          disabled={needsInstrumentChoice && !selectedAsignacionId}><FaDownload />Descargar carpeta completa
                                                  </Button>
                      ) : null}
                    </VStack>
                    <ButtonGroup size="sm" attached variant="outline" flexShrink={0}>
                      <IconButton
                        aria-label="Vista lista"
                        onClick={() => persistVista('list')}
                        variant={vista === 'list' ? 'solid' : 'outline'}
                        colorPalette="purple"><FaList /></IconButton>
                      <IconButton
                        aria-label="Vista mosaico"
                        onClick={() => persistVista('mosaic')}
                        variant={vista === 'mosaic' ? 'solid' : 'outline'}
                        colorPalette="purple"><FaTh /></IconButton>
                    </ButtonGroup>
                  </HStack>

                  {needsInstrumentChoice && !selectedAsignacionId ? (
                    <Text fontSize="sm" color="gray.600">
                      Elige tu instrumento arriba para filtrar las partituras de tu voz.
                    </Text>
                  ) : obrasLoading ? (
                    <Flex justify="center" py={16}>
                      <Spinner color="purple.500" size="lg" />
                    </Flex>
                  ) : obras.length === 0 ? (
                    <Text fontSize="sm" color="gray.500">
                      No hay partituras en esta carpeta para tu instrumento, o aún no están listas.
                    </Text>
                  ) : vista === 'mosaic' ? (
                    <SimpleGrid columns={{ base: 2, sm: 3, md: 4 }} gap={4}>
                      {obras.map((o, idx) => {
                        const orden = Number.isFinite(Number(o?.orden)) ? Number(o.orden) : idx + 1;
                        return (
                          <Card.Root
                            key={o.id}
                            bg={cardBg}
                            shadow="md"
                            rounded="xl"
                            overflow="hidden"
                            position="relative"
                            cursor={visorLoading ? 'wait' : 'pointer'}
                            onClick={() => !visorLoading && abrirObra(o.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!visorLoading) abrirObra(o.id);
                              }
                            }}
                            _hover={{ shadow: 'lg' }}
                          >
                            <AspectRatio ratio={3 / 4}>
                              <Box bg="gray.100" position="relative">
                                {o.miniatura?.imagenUrl ? (
                                  <Image
                                    src={pageImageSrc(o.miniatura)}
                                    alt={o.titulo}
                                    objectFit="cover"
                                    w="100%"
                                    h="100%"
                                    transform={`rotate(${o.miniatura.rotacion || 0}deg)`}
                                  />
                                ) : (
                                  <Flex align="center" justify="center" h="full">
                                    <Text fontSize="xs" color="gray.500">
                                      Sin vista
                                    </Text>
                                  </Flex>
                                )}
                                {o.tieneNotasDirector ? (
                                  <Badge
                                    position="absolute"
                                    top={2}
                                    right={2}
                                    colorPalette="yellow"
                                    fontSize="0.65rem"
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <Icon asChild><FaStickyNote /></Icon>
                                    Notas
                                  </Badge>
                                ) : null}
                              </Box>
                            </AspectRatio>
                            <Card.Body pt={2}>
                              <HStack align="flex-start" gap={2} mb={1}>
                                <Text fontWeight="700" fontSize="sm" lineClamp={2} flex={1} minW={0}>
                                  {orden}. {o.titulo}
                                </Text>
                                <HStack gap={1} flexShrink={0}>
                                  {o.youtubeUrl ? (
                                    <IconButton
                                      isExternal
                                      aria-label={`Ver vídeo en YouTube: ${o.titulo}`}
                                      size="xs"
                                      variant="outline"
                                      colorPalette="red"
                                      asChild><Link href={o.youtubeUrl} onClick={(e) => e.stopPropagation()}><FaYoutube /></Link></IconButton>
                                  ) : null}
                                  <IconButton
                                    aria-label={`Descargar PDF: ${o.titulo}`}
                                    size="xs"
                                    variant="outline"
                                    colorPalette="purple"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      downloadObraPdf(o.id, o.titulo);
                                    }}
                                    loading={obraPdfLoadingId === o.id}
                                    disabled={needsInstrumentChoice && !selectedAsignacionId}><FaDownload /></IconButton>
                                </HStack>
                              </HStack>
                              {o.autor ? (
                                <Text fontSize="xs" color="gray.500" lineClamp={1}>
                                  {o.autor}
                                </Text>
                              ) : null}
                              {o.notasDirector?.trim() ? (
                                <Text
                                  fontSize="xs"
                                  color="gray.600"
                                  mt={1.5}
                                  lineClamp={3}
                                  whiteSpace="pre-wrap"
                                  wordBreak="break-word"
                                >
                                  {o.notasDirector.trim()}
                                </Text>
                              ) : null}
                            </Card.Body>
                          </Card.Root>
                        );
                      })}
                    </SimpleGrid>
                  ) : (
                    <VStack align="stretch" gap={3}>
                      {obras.map((o, idx) => {
                        const orden = Number.isFinite(Number(o?.orden)) ? Number(o.orden) : idx + 1;
                        return (
                          <Card.Root
                            key={o.id}
                            bg={cardBg}
                            shadow="sm"
                            rounded="lg"
                            cursor={visorLoading ? 'wait' : 'pointer'}
                            onClick={() => !visorLoading && abrirObra(o.id)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                if (!visorLoading) abrirObra(o.id);
                              }
                            }}
                            _hover={{ shadow: 'md' }}
                          >
                            <Card.Body py={3}>
                              <HStack align="start" gap={4}>
                                <Box w="56px" flexShrink={0} position="relative">
                                  <AspectRatio ratio={3 / 4}>
                                    <Box bg="gray.100" rounded="md" overflow="hidden">
                                      {o.miniatura?.imagenUrl ? (
                                        <Image
                                          src={pageImageSrc(o.miniatura)}
                                          alt=""
                                          objectFit="cover"
                                          w="100%"
                                          h="100%"
                                          transform={`rotate(${o.miniatura.rotacion || 0}deg)`}
                                        />
                                      ) : null}
                                    </Box>
                                  </AspectRatio>
                                  {o.tieneNotasDirector ? (
                                    <Badge
                                      position="absolute"
                                      top={-1}
                                      right={-1}
                                      colorPalette="yellow"
                                      fontSize="0.6rem"
                                      borderRadius="md"
                                    >
                                      <Icon mr={1} asChild><FaStickyNote /></Icon>
                                      Notas
                                    </Badge>
                                  ) : null}
                                </Box>
                                <Box minW={0} flex={1}>
                                  <HStack align="flex-start" gap={2} mb={0.5}>
                                    <Text fontWeight="700" fontSize="md" flex={1} minW={0} lineClamp={2}>
                                      {orden}. {o.titulo}
                                    </Text>
                                    <HStack gap={1} flexShrink={0}>
                                      {o.youtubeUrl ? (
                                        <IconButton
                                          isExternal
                                          aria-label={`Ver vídeo en YouTube: ${o.titulo}`}
                                          size="sm"
                                          variant="outline"
                                          colorPalette="red"
                                          asChild><Link href={o.youtubeUrl} onClick={(e) => e.stopPropagation()}><FaYoutube /></Link></IconButton>
                                      ) : null}
                                      <IconButton
                                        aria-label={`Descargar PDF: ${o.titulo}`}
                                        size="sm"
                                        variant="outline"
                                        colorPalette="purple"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          downloadObraPdf(o.id, o.titulo);
                                        }}
                                        loading={obraPdfLoadingId === o.id}
                                        disabled={needsInstrumentChoice && !selectedAsignacionId}><FaDownload /></IconButton>
                                    </HStack>
                                  </HStack>
                                  {o.autor ? (
                                    <Text fontSize="sm" color="gray.600">
                                      {o.autor}
                                    </Text>
                                  ) : null}
                                  {o.genero?.nombre ? (
                                    <Text fontSize="xs" color="gray.500" mt={1}>
                                      {o.genero.nombre}
                                    </Text>
                                  ) : null}
                                  {o.notasDirector?.trim() ? (
                                    <Text
                                      fontSize="sm"
                                      color="gray.600"
                                      mt={2}
                                      lineClamp={4}
                                      whiteSpace="pre-wrap"
                                      wordBreak="break-word"
                                    >
                                      {o.notasDirector.trim()}
                                    </Text>
                                  ) : null}
                                </Box>
                              </HStack>
                            </Card.Body>
                          </Card.Root>
                        );
                      })}
                    </VStack>
                  )}
                </Box>
              </Flex>
            </Container>
          </Box>

          <Box mt="auto">
            <Footer />
          </Box>
        </Flex>
      ) : null}

      {visorDetail ? (
        <Portal>
          <Box
            position="fixed"
            inset={0}
            zIndex={9999}
            bg="black"
            overflow="hidden"
            overscrollBehavior="none"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              maxWidth: '100%',
              maxHeight: '100%',
              zIndex: 9999,
              backgroundColor: '#000',
              touchAction: 'none',
              // Evitar 100vw/100dvw: en móvil generan overflow y Safari “hace zoom” a toda la página.
            }}
          >
            {visorLoading || (visorDetail && !visorDocReady) ? (
              <Flex
                position="absolute"
                inset={0}
                align="center"
                justify="center"
                direction="column"
                gap={3}
                bg="blackAlpha.800"
                zIndex={50}
                pointerEvents="all"
              >
                <Spinner color="purple.300" size="lg" borderWidth="3px" />
                <Text color="whiteAlpha.800" fontSize="sm">
                  {visorLoading ? 'Cargando partituras…' : 'Preparando documento…'}
                </Text>
              </Flex>
            ) : null}
            {visorPaginaActual && visorChromeIsShown(visorChromeVisible) ? (
              isMobileVisor ? (
                <Box
                  data-visor-chrome
                  position="absolute"
                  inset={0}
                  pointerEvents="none"
                  zIndex={20}
                >
                  <Text
                    position="absolute"
                    left={3}
                    fontSize="xs"
                    fontWeight="600"
                    color="white"
                    bg="blackAlpha.600"
                    px={2}
                    py={1}
                    borderRadius="md"
                    pointerEvents="none"
                    letterSpacing="wide"
                    css={{
                      top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                    }}
                  >
                    {visorPageIndex + 1} - {visorPaginas.length}
                  </Text>
                  <IconButton
                    aria-label="Cerrar"
                    onClick={cerrarVisor}
                    position="absolute"
                    right={3}
                    size="md"
                    variant="solid"
                    colorPalette="blackAlpha"
                    bg="blackAlpha.700"
                    color="white"
                    pointerEvents="auto"
                    borderRadius="full"
                    css={{
                      top: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                    }}
                  >
                    <FaTimes />
                  </IconButton>
                </Box>
              ) : (
            <HStack
              data-visor-chrome
              position="absolute"
              top={0}
              left={0}
              right={0}
              px={{ base: 2, md: 4 }}
              py={{ base: 1.5, md: 3 }}
              zIndex={20}
              bg="blackAlpha.700"
              backdropFilter="blur(8px)"
              justify="space-between"
              align="center"
              gap={{ base: 1, md: 2 }}
              css={{
                pt: 'calc(env(safe-area-inset-top, 0px) + 0.375rem)',

                '& @media (min-width: 48em)': {
                  pt: 'calc(env(safe-area-inset-top, 0px) + 0.75rem)',
                }
              }}
            >
              <Button
                size={{ base: 'xs', md: 'sm' }}
                colorPalette="purple"
                variant="solid"
                onClick={cerrarVisor}
                flexShrink={0}
              >
                <Text as="span" display={{ base: 'none', sm: 'inline' }}>
                  Volver al listado
                </Text>
                <Text as="span" display={{ base: 'inline', sm: 'none' }}>
                  Volver
                </Text>
              </Button>
              <Text
                color="white"
                fontSize={{ base: 'xs', md: 'sm' }}
                fontWeight="600"
                textAlign="center"
                flex="1"
                lineClamp={{ base: 1, md: 2 }}
                px={{ base: 1, md: 2 }}
                minW={0}
              >
                {visorTituloActual}
                {visorAutorActual ? (
                  <Text
                    as="span"
                    display={{ base: 'none', md: 'block' }}
                    fontWeight="400"
                    fontSize="xs"
                    color="whiteAlpha.800"
                  >
                    {visorAutorActual}
                  </Text>
                ) : null}
              </Text>
              <HStack gap={2} flexShrink={0} minW={{ base: 'auto', md: 'fit-content' }}>
                {hayAlgunaNota ? (
                  <>
                    <IconButton
                      aria-label={`Notas del director (${notasBloques.length})`}
                      size="sm"
                      variant="outline"
                      borderColor="yellow.300"
                      color="yellow.100"
                      _hover={{ bg: 'whiteAlpha.200' }}
                      onClick={onNotasModalOpen}
                      display={{ base: 'inline-flex', md: 'none' }}><FaStickyNote /></IconButton>
                    <Button
                      size="sm"
                      variant="outline"
                      borderColor="yellow.300"
                      color="yellow.100"
                      _hover={{ bg: 'whiteAlpha.200' }}
                      onClick={onNotasModalOpen}
                      display={{ base: 'none', md: 'inline-flex' }}><FaStickyNote />
                        Notas del director
                        <Badge ml={2} colorPalette="yellow" borderRadius="full">
                      {notasBloques.length}
                    </Badge></Button>
                  </>
                ) : null}
              </HStack>
            </HStack>
              )
            ) : null}

            {visorPaginas.length > 0 ? (
              <Box
                key={visorSessionKey}
                ref={visorScrollRef}
                position="absolute"
                inset={0}
                overflow="auto"
                overscrollBehavior="none"
                cursor={visorZoom > 1.02 ? 'grab' : 'auto'}
                pointerEvents={visorDocReady ? 'auto' : 'none'}
                opacity={visorDocReady ? 1 : 0.35}
                css={{
                  WebkitOverflowScrolling: 'touch',
                  // Siempre none: pan-y rompe el pellizco en página 1 y en horizontal (iOS).
                  touchAction: 'none',
                  scrollbarGutter: 'stable',
                }}
                onClick={(e) => {
                  if (!visorDocReady) return;
                  if (e.target?.closest?.('[data-visor-chrome]')) return;
                  if (visorTapRef.current.moved || visorPinchRef.current.active) return;
                  if (!isFinePointerDesktop()) setVisorChromeVisible((v) => !v);
                }}
                onMouseDown={(e) => {
                  if (visorZoom <= 1.02) return;
                  const el = e.currentTarget;
                  visorDragRef.current.down = true;
                  visorDragRef.current.startX = e.clientX;
                  visorDragRef.current.startY = e.clientY;
                  visorDragRef.current.startScrollLeft = el.scrollLeft;
                  visorDragRef.current.startScrollTop = el.scrollTop;
                }}
                onMouseMove={(e) => {
                  if (!visorDragRef.current.down) return;
                  const el = e.currentTarget;
                  const dx = e.clientX - visorDragRef.current.startX;
                  const dy = e.clientY - visorDragRef.current.startY;
                  el.scrollLeft = visorDragRef.current.startScrollLeft - dx;
                  el.scrollTop = visorDragRef.current.startScrollTop - dy;
                }}
                onMouseUp={() => {
                  visorDragRef.current.down = false;
                }}
                onMouseLeave={() => {
                  visorDragRef.current.down = false;
                }}
              >
                <Box
                  position="relative"
                  flexShrink={0}
                  style={{
                    width: `${Math.max(
                      (visorContentSize.w || visorViewBox.cw || 0) * visorZoom,
                      visorViewBox.cw || 0,
                    )}px`,
                    height:
                      visorContentSize.h > 0
                        ? `${visorContentSize.h * visorZoom}px`
                        : undefined,
                  }}
                >
                  <Flex
                    ref={visorContentRef}
                    direction="column"
                    align="center"
                    gap={`${PAGE_GAP}px`}
                    py={4}
                    px={2}
                    w={visorViewBox.cw ? `${visorViewBox.cw}px` : '100%'}
                    maxW="none"
                    style={{
                      transform: `scale(${visorZoom})`,
                      transformOrigin: 'top left',
                      willChange: visorZoom === 1 ? 'auto' : 'transform',
                    }}
                  >
                  {visorPaginas.map((p, i) => {
                    const pageKey = visorPageKey(p, i);
                    const lm = visorPageLayouts[pageKey];
                    if (!lm) return null;
                    const { rot, nw, nh, dw, dh, bbox } = lm;
                    const hasSized =
                      nw > 0 &&
                      nh > 0 &&
                      dw != null &&
                      dh != null &&
                      bbox;
                    const prev = i > 0 ? visorPaginas[i - 1] : null;
                    const showObraBreak =
                      prev &&
                      prev.obraId != null &&
                      p.obraId != null &&
                      String(prev.obraId) !== String(p.obraId);
                    return (
                      <React.Fragment key={pageKey}>
                        {showObraBreak ? (
                          <Text
                            color="whiteAlpha.700"
                            fontSize="xs"
                            fontWeight="600"
                            textAlign="center"
                            w="100%"
                            py={1}
                            px={2}
                          >
                            {p.obraTitulo || 'Siguiente obra'}
                            {p.obraAutor ? ` — ${p.obraAutor}` : ''}
                          </Text>
                        ) : null}
                      <Box
                        data-visor-page-id={pageKey}
                        ref={(el) => {
                          if (el) visorPageElsRef.current.set(pageKey, el);
                          else visorPageElsRef.current.delete(pageKey);
                        }}
                        flexShrink={0}
                        mx="auto"
                        w={hasSized ? `${bbox.w}px` : '100%'}
                        h={hasSized ? `${bbox.h}px` : undefined}
                        minW={hasSized ? `${bbox.w}px` : 0}
                        minH={hasSized ? `${bbox.h}px` : undefined}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        pointerEvents="none"
                        overflow="hidden"
                      >
                        <img
                          src={pageImageSrc(p)}
                          alt={`Partitura orden ${p.orden}`}
                          draggable={false}
                          ref={(img) => {
                            if (!img?.complete) return;
                            // Imágenes en caché no disparan onLoad; leer naturales al montar.
                            Promise.resolve().then(() => {
                              rememberImgNatural(img, pageKey, setVisorImgNaturals);
                            });
                          }}
                          onLoad={(e) => {
                            rememberImgNatural(e.currentTarget, pageKey, setVisorImgNaturals);
                          }}
                          style={
                            hasSized
                              ? {
                                  display: 'block',
                                  width: dw,
                                  height: dh,
                                  maxWidth: 'unset',
                                  maxHeight: 'unset',
                                  objectFit: 'fill',
                                  transform: rot ? `rotate(${rot}deg)` : undefined,
                                  transformOrigin: 'center',
                                  pointerEvents: 'none',
                                  userSelect: 'none',
                                }
                              : {
                                  display: 'block',
                                  maxWidth: '100%',
                                  maxHeight: visorViewBox.ch
                                    ? `${visorViewBox.ch}px`
                                    : '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  transform: rot ? `rotate(${rot}deg)` : undefined,
                                  transformOrigin: 'center',
                                  pointerEvents: 'none',
                                  userSelect: 'none',
                                }
                          }
                        />
                      </Box>
                      </React.Fragment>
                    );
                  })}
                  </Flex>
                </Box>
              </Box>
            ) : null}

            {visorPaginaActual && visorChromeIsShown(visorChromeVisible) && !isMobileVisor ? (
              <VStack
                data-visor-chrome
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                py={{ base: 2, md: 3 }}
                px={{ base: 2, md: 4 }}
                bg="blackAlpha.700"
                backdropFilter="blur(8px)"
                gap={1}
                zIndex={20}
                css={{
                  pb: 'calc(env(safe-area-inset-bottom, 0px) + 0.5rem)',

                  '& @media (min-width: 48em)': {
                    pb: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)',
                  }
                }}
              >
                <Text textAlign="center" fontSize="sm" color="gray.300" px={2} lineClamp={2}>
                  {visorTituloActual ? `${visorTituloActual} · ` : ''}
                  orden {visorPaginaActual.orden} · {visorPageIndex + 1}/{visorPaginas.length}
                </Text>
                <HStack justify="center" gap={2} flexWrap="wrap">
                  <IconButton
                    aria-label="Reducir zoom"
                    size="sm"
                    variant="outline"
                    borderColor="whiteAlpha.400"
                    color="white"
                    onClick={visorZoomOut}
                    disabled={visorZoom <= VISOR_ZOOM_MIN}><FaSearchMinus /></IconButton>
                  <Text fontSize="xs" color="gray.300" minW="52px" textAlign="center">
                    {Math.round(visorZoom * 100)}%
                  </Text>
                  <IconButton
                    aria-label="Aumentar zoom"
                    size="sm"
                    variant="outline"
                    borderColor="whiteAlpha.400"
                    color="white"
                    onClick={visorZoomIn}
                    disabled={visorZoom >= VISOR_ZOOM_MAX}><FaSearchPlus /></IconButton>
                  <Button
                    size="xs"
                    variant="ghost"
                    color="gray.400"
                    onClick={() => {
                      setVisorZoom(1);
                      requestAnimationFrame(() => {
                        scrollVisorToPageIndex(visorPageIndex);
                      });
                    }}
                    disabled={visorZoom === 1}
                  >
                    Ajustar a pantalla
                  </Button>
                </HStack>
                <Box w="full" maxW={{ base: '340px', md: '420px' }} px={2}>
                  <SliderRoot
                    aria-label="Zoom"
                    min={VISOR_ZOOM_MIN}
                    max={VISOR_ZOOM_MAX}
                    step={0.01}
                    value={visorZoom}
                    onValueChange={(v) => setVisorZoom(clampVisorZoom(v))}
                  >
                    <SliderTrack bg="whiteAlpha.200">
                      <SliderFilledTrack bg="purple.400" />
                    </SliderTrack>
                    <SliderThumb />
                  </SliderRoot>
                </Box>
                <Text fontSize="xs" color="gray.500" textAlign="center" px={2}>
                  Ctrl+rueda o slider para zoom · flechas página · Esc listado
                </Text>
                {notaEnPaginaActual ? (
                  <Button size="xs" variant='plain' color="yellow.200" onClick={onNotasModalOpen}>
                    Ver nota del director en esta página
                  </Button>
                ) : hayAlgunaNota ? (
                  <Button size="xs" variant='plain' color="gray.400" onClick={onNotasModalOpen}>
                    Ver notas del director
                  </Button>
                ) : null}
              </VStack>
            ) : null}
          </Box>
        </Portal>
      ) : null}

      <Dialog.Root open={notasModalOpen} size='lg' placement='center' scrollBehavior="inside" onOpenChange={e => {
        if (!e.open) {
          onNotasModalClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop zIndex={10050} />
          <Dialog.Positioner>
            <Dialog.Content mx={4} zIndex={10051}>
              <Dialog.Header>Notas del director</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                {notasBloques.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    No hay notas en las páginas de esta carpeta.
                  </Text>
                ) : (
                  <VStack align="stretch" gap={6}>
                    {notasBloques.map((b, idx) => (
                      <Box key={b.key}>
                        <Text fontWeight="700" fontSize="sm" color="purple.600" mb={2}>
                          {b.titulo}
                        </Text>
                        <Text whiteSpace="pre-wrap" fontSize="sm" color="gray.700">
                          {b.texto}
                        </Text>
                        {idx < notasBloques.length - 1 ? <Separator mt={4} /> : null}
                      </Box>
                    ))}
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button onClick={onNotasModalClose}>Cerrar</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={listadoObrasModalOpen}
        placement='center'
        size='lg'
        scrollBehavior="inside"
        onOpenChange={e => {
          if (!e.open) {
            onListadoObrasModalClose();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>
                Listado de obras
                {selectedCarpeta?.nombre ? (
                  <Text as="span" display="block" fontSize="sm" fontWeight="normal" color="gray.600" mt={1}>
                    {selectedCarpeta.nombre}
                  </Text>
                ) : null}
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Alert.Root
                  status="info"
                  variant='subtle'
                  borderRadius="md"
                  mb={4}
                  borderStartWidth='3px'
                  borderStartColor='colorPalette.solid'>
                  <Alert.Indicator />
                  <Text fontSize="sm">
                    Si alguna obra del listado no aparece en tu carpeta o alguna voz no corresponde con la tuya, notifica la
                    incidencia al administrador de la cuenta de tu banda.
                  </Text>
                </Alert.Root>
                {listadoObrasLoading ? (
                  <Flex justify="center" py={10}>
                    <Spinner color="purple.500" />
                  </Flex>
                ) : listadoObrasReferencia.length === 0 ? (
                  <Text fontSize="sm" color="gray.600">
                    No hay obras listas en esta carpeta.
                  </Text>
                ) : (
                  <VStack align="stretch" gap={0} maxH="50vh" overflowY="auto">{listadoObrasReferencia.map((o, idx) => {
                      const orden = Number.isFinite(Number(o?.orden)) ? Number(o.orden) : idx + 1;
                      return (
                        <HStack key={o.id} py={2.5} gap={3} align="flex-start">
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            color="purple.600"
                            minW="2rem"
                            textAlign="right"
                            fontVariantNumeric="tabular-nums"
                          >
                            {orden}.
                          </Text>
                          <Box flex={1} minW={0}>
                            <Text fontSize="sm" fontWeight="600">
                              {o.titulo || 'Sin título'}
                            </Text>
                            {o.autor ? (
                              <Text fontSize="xs" color="gray.600" mt={0.5}>
                                {o.autor}
                              </Text>
                            ) : null}
                          </Box>
                        </HStack>
                      );
                    })}</VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                <Button onClick={onListadoObrasModalClose}>Cerrar</Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={descargaCarpetaModal.open} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          descargaCarpetaModal.onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Descargar carpeta completa</Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body>
                <Text fontSize="sm" color="gray.600" mb={3}>
                  ¿Cómo quieres descargar la carpeta?
                </Text>
                <VStack align="stretch" gap={2}>
                  <Button
                    variant={descargaCarpetaModo === 'pdf' ? 'solid' : 'outline'}
                    colorPalette="purple"
                    justifyContent="flex-start"
                    onClick={() => setDescargaCarpetaModo('pdf')}
                  >
                    Un solo fichero PDF
                  </Button>
                  <Button
                    variant={descargaCarpetaModo === 'zip' ? 'solid' : 'outline'}
                    colorPalette="purple"
                    justifyContent="flex-start"
                    onClick={() => setDescargaCarpetaModo('zip')}
                  >
                    Obras por separado (ZIP)
                  </Button>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" mr={3} onClick={descargaCarpetaModal.onClose}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={() => {
                    if (!selectedCarpetaId) return;
                    const nombre = selectedCarpeta?.nombre || 'carpeta';
                    descargaCarpetaModal.onClose();
                    if (descargaCarpetaModo === 'zip') {
                      void downloadCarpetaZip(selectedCarpetaId, nombre);
                    } else {
                      void downloadCarpetaPdf(selectedCarpetaId, nombre);
                    }
                  }}
                  loading={
                    (selectedCarpetaId && carpetaPdfLoadingId === selectedCarpetaId) ||
                    (selectedCarpetaId && carpetaZipLoadingId === selectedCarpetaId)
                  }
                >
                  Descargar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <RepertorioIncidenciaReportModal
        isOpen={incidenciaModalOpen}
        onClose={onIncidenciaModalClose}
        token={token}
        accessToken={accessToken}
        carpetas={carpetas}
        initialCarpetaId={selectedCarpetaId}
        selectedMusico={selectedMusico}
        asignaciones={asignaciones}
        selectedAsignacionId={selectedAsignacionId}
        partituraInstrumentSlug={partituraInstrumentSlug}
        partituraVozEtiquetasCsv={partituraVozEtiquetasCsv}
      />
    </>
  );
}
